import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
function parseMarkdown(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color:#F97316;text-decoration:underline;">$1</a>')
    .replace(/^## (.+)$/gm, "<h2 style='font-size:20px;font-weight:700;color:#111;margin:1.5rem 0 0.5rem'>$1</h2>")
    .replace(/^### (.+)$/gm, "<h3 style='font-size:17px;font-weight:700;color:#111;margin:1.25rem 0 0.5rem'>$1</h3>")
    .replace(/^- (.+)$/gm, "<li style='margin:4px 0;'>$1</li>")
    .replace(/\n\n/g, "<br/><br/>");
}
const API_URL = process.env.REACT_APP_API_URL || "https://renthound-backend-production.up.railway.app";
const PAGE_SIZE = 20;
const sourceLabel = { realtyapi: "Redfin", zillow: "Zillow", realtor: "Realtor.com", apartments: "Apartments.com", airbnb: "Airbnb", user: "Owner" };
const sourceColor = { realtyapi: "#1A56DB", zillow: "#006AFF", realtor: "#D92228", apartments: "#5E8E3E", airbnb: "#FF5A5F", user: "#9061F9" };

const CITIES = {
  philadelphia: {
    name: "Philadelphia, PA", slug: "philadelphia",
    headline: "Apartments & Houses for Rent in Philadelphia",
    description: "Find rentals in Philadelphia, PA — from Center City apartments to rowhouses in South Philly, Fishtown, Manayunk, and beyond. Search listings from Redfin, Zillow, and local landlords all in one place.",
    neighborhoods: ["Center City", "Fishtown", "South Philly", "Manayunk", "Northern Liberties", "West Philly"],
    avgRent: "$1,800",
  },
  "new-york": {
    name: "New York, NY", slug: "new-york",
    headline: "Apartments & Houses for Rent in New York City",
    description: "Search NYC rentals across Manhattan, Brooklyn, Queens, the Bronx, and Staten Island. Find apartments, studios, and houses for rent from top listing platforms and local landlords.",
    neighborhoods: ["Manhattan", "Brooklyn", "Queens", "Bronx", "Harlem", "Astoria"],
    avgRent: "$3,500",
  },
  "los-angeles": {
    name: "Los Angeles, CA", slug: "los-angeles",
    headline: "Apartments & Houses for Rent in Los Angeles",
    description: "Find rentals in Los Angeles — from Hollywood apartments to Silver Lake bungalows, Santa Monica beach units, and houses in the San Fernando Valley.",
    neighborhoods: ["Hollywood", "Silver Lake", "Santa Monica", "West Hollywood", "Downtown LA", "Venice"],
    avgRent: "$2,800",
  },
  chicago: {
    name: "Chicago, IL", slug: "chicago",
    headline: "Apartments & Houses for Rent in Chicago",
    description: "Search Chicago rentals in Lincoln Park, Wicker Park, Logan Square, the Loop, and more.",
    neighborhoods: ["Lincoln Park", "Wicker Park", "Logan Square", "The Loop", "Lakeview", "River North"],
    avgRent: "$2,000",
  },
  houston: {
    name: "Houston, TX", slug: "houston",
    headline: "Apartments & Houses for Rent in Houston",
    description: "Find rentals in Houston, TX — from Midtown apartments to homes in The Heights, Montrose, and the Energy Corridor.",
    neighborhoods: ["Midtown", "The Heights", "Montrose", "River Oaks", "Museum District", "Galleria"],
    avgRent: "$1,500",
  },
  phoenix: {
    name: "Phoenix, AZ", slug: "phoenix",
    headline: "Apartments & Houses for Rent in Phoenix",
    description: "Search rentals in Phoenix, AZ including Scottsdale, Tempe, Mesa, and Chandler.",
    neighborhoods: ["Scottsdale", "Tempe", "Mesa", "Chandler", "Downtown Phoenix", "Arcadia"],
    avgRent: "$1,600",
  },
  miami: {
    name: "Miami, FL", slug: "miami",
    headline: "Apartments & Houses for Rent in Miami",
    description: "Find rentals in Miami, FL — from Brickell high-rises to Wynwood lofts, Coral Gables homes, and South Beach apartments.",
    neighborhoods: ["Brickell", "Wynwood", "South Beach", "Coral Gables", "Coconut Grove", "Midtown"],
    avgRent: "$2,500",
  },
  atlanta: {
    name: "Atlanta, GA", slug: "atlanta",
    headline: "Apartments & Houses for Rent in Atlanta",
    description: "Search Atlanta rentals in Midtown, Buckhead, Little Five Points, Old Fourth Ward, and more.",
    neighborhoods: ["Midtown", "Buckhead", "Little Five Points", "Old Fourth Ward", "Decatur", "Inman Park"],
    avgRent: "$1,700",
  },
  boston: {
    name: "Boston, MA", slug: "boston",
    headline: "Apartments & Houses for Rent in Boston",
    description: "Find rentals in Boston, MA — from Back Bay apartments to Cambridge townhouses, Somerville homes, and South End condos.",
    neighborhoods: ["Back Bay", "South End", "Cambridge", "Somerville", "Allston", "Beacon Hill"],
    avgRent: "$3,000",
  },
  denver: {
    name: "Denver, CO", slug: "denver",
    headline: "Apartments & Houses for Rent in Denver",
    description: "Search Denver rentals in Capitol Hill, LoDo, Highland, Washington Park, and more.",
    neighborhoods: ["Capitol Hill", "LoDo", "Highland", "Washington Park", "RiNo", "Cherry Creek"],
    avgRent: "$1,900",
  },
  orlando: {
    name: "Orlando, FL", slug: "orlando",
    headline: "Apartments & Houses for Rent in Orlando",
    description: "Find rentals in Orlando, FL — from Downtown and Lake Nona apartments to homes in Baldwin Park, Winter Park, and Kissimmee.",
    neighborhoods: ["Downtown Orlando", "Lake Nona", "Baldwin Park", "Winter Park", "Dr. Phillips", "Kissimmee"],
    avgRent: "$1,800",
  },
};

const supabase = createClient(
  "https://nbvighcligjzdimkpdhq.supabase.co",
  "sb_publishable_zDP7A3xgD2PJKaRJztP4_A_UR-N7ol-"
);

// ─── SEO tags ─────────────────────────────────────────────────────────────────
// One place that sets the title, description, canonical URL, social preview tags and
// structured data for the current page. The Netlify edge function (netlify/edge-functions/seo.js)
// puts the same tags into the HTML before it's sent, for crawlers that don't run JavaScript.
const SITE_URL = "https://renthound.com";
const SEO_DEFAULTS = {
  title: "RentHound — Find Apartments, Houses & Rentals Near You",
  description: "Search rentals from Redfin, Zillow, Realtor.com, Apartments.com and independent landlords all in one place. Find apartments, houses, and storage units near you.",
  path: "/",
  image: `${SITE_URL}/og-image.png`,
  type: "website",
};

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
}

function applySeo({ title, description, path, image, type, jsonLd, noindex } = {}) {
  const t = title || SEO_DEFAULTS.title;
  const d = description || SEO_DEFAULTS.description;
  const url = SITE_URL + (path || SEO_DEFAULTS.path);
  const img = image || SEO_DEFAULTS.image;
  document.title = t;
  setMeta("name", "description", d);
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
  canonical.href = url;
  setMeta("property", "og:title", t);
  setMeta("property", "og:description", d);
  setMeta("property", "og:url", url);
  setMeta("property", "og:type", type || SEO_DEFAULTS.type);
  setMeta("property", "og:image", img);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", t);
  setMeta("name", "twitter:description", d);
  setMeta("name", "twitter:image", img);
  setMeta("name", "robots", noindex ? "noindex" : "index, follow");
  document.getElementById("page-schema")?.remove();
  if (jsonLd) {
    const script = document.createElement("script");
    script.id = "page-schema"; script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }
}

// Sets this page's tags while it's shown, and puts the homepage's back when it's left.
function useSeo(seo) {
  const key = JSON.stringify(seo);
  useEffect(() => {
    applySeo(JSON.parse(key));
    return () => applySeo();
  }, [key]);
}

function getCityFromPath() {
  const path = window.location.pathname;
  const match = path.match(/^\/rentals\/([a-z-]+)/);
  if (match && CITIES[match[1]]) return CITIES[match[1]];
  return null;
}

// Paths the app has a page for. Anything else gets "Page not found" (and a 404 from netlify/edge-functions/seo.js).
const APP_PAGES = ["/pricing", "/about", "/privacy", "/terms", "/blog", "/saved"];
function isKnownPath() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  return path === "/" || path === "/index.html" || APP_PAGES.includes(path) || path.startsWith("/blog/") || !!getCityFromPath();
}

// The published blog posts (no content), fetched once and shared by the pages that link to them.
let blogListRequest = null;
function fetchBlogList() {
  if (!blogListRequest) {
    blogListRequest = fetch(`${API_URL}/api/blog`).then(r => r.json()).then(d => d.posts || [])
      .catch(() => { blogListRequest = null; return []; });
  }
  return blogListRequest;
}

// A post belongs to a city when its address names the city, e.g. eviction-process-denver.
// The posts' own city field isn't filled in consistently. Keep in sync with cityGuides in seo.js.
const CITY_GUIDE_ALIASES = { philadelphia: ["pennsylvania"] };
const cityGuides = (posts, slug) => posts.filter(post => [slug, ...(CITY_GUIDE_ALIASES[slug] || [])].some(key => post.slug.includes(key)));

const openBlogPost = (setPage, slug) => (e) => {
  e.preventDefault();
  window.history.pushState({}, "", `/blog/${slug}`);
  setPage("blogpost");
  window.scrollTo(0, 0);
};

function getPageFromPath() {
  const path = window.location.pathname;
  if (path === "/pricing") return "pricing";
  if (path === "/about") return "about";
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  if (path === "/blog") return "blog";
  if (path === "/saved") return "saved";
  if (path.startsWith("/blog/")) return "blogpost";
  return null;
}

function pageFromLocation() {
  if (window.location.search.includes("admin")) return "admin";
  return getPageFromPath() || (getCityFromPath() ? "city" : isKnownPath() ? "home" : "notfound");
}

// The address each page lives at. Dashboard and admin have none of their own.
const PAGE_PATHS = { home: "/", about: "/about", pricing: "/pricing", privacy: "/privacy", terms: "/terms", blog: "/blog", saved: "/saved" };

function getBlogSlugFromPath() {
  const path = window.location.pathname;
  const match = path.match(/^\/blog\/(.+)/);
  return match ? match[1] : null;
}

// ─── Blog Pages ───────────────────────────────────────────────────────────────

function BlogPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useSeo({ title: "Rental Resources & Guides — RentHound Blog", description: "Guides for renters and landlords: average rent by city, eviction rules by state, rental scams to avoid, and tips for screening tenants and writing leases.", path: "/blog" });

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog`);
        const data = await res.json();
        setPosts(data.posts || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchPosts();
  }, []);

  const categories = ["all", "tenant-rights", "landlord-tips", "city-guides", "legal-resources"];
  const filtered = activeCategory === "all" ? posts : posts.filter(p => p.category === activeCategory);

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ background: "linear-gradient(135deg, #0f1f3d 0%, #1a3566 100%)", padding: "4rem 2rem", textAlign: "center" }}>
        <h1 style={{ color: "white", fontSize: 38, fontWeight: 700, margin: "0 0 1rem" }}>Rental Resources & Guides</h1>
        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 17, maxWidth: 600, margin: "0 auto" }}>Tenant rights, landlord tips, eviction laws, and rental guides — by state and city.</p>
      </div>
      <div style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "1rem 2rem" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {categories.map(c => (
            <button key={c} onClick={() => setActiveCategory(c)}
              style={{ background: activeCategory === c ? "#0f1f3d" : "white", color: activeCategory === c ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 20, padding: "6px 16px", fontSize: 13, fontWeight: 500, cursor: "pointer", textTransform: "capitalize" }}>
              {c === "all" ? "All Posts" : c.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>
        {loading ? <div style={{ textAlign: "center", padding: "4rem 0", color: "#6b7280" }}>Loading posts...</div>
        : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem 0", color: "#6b7280" }}>
            <IconBadge name="file" />
            <p style={{ fontSize: 16 }}>No posts yet. Check back soon!</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.5rem" }}>
            {filtered.map(post => (
              <div key={post.id} onClick={() => { window.history.pushState({}, "", `/blog/${post.slug}`); setPage("blogpost"); }}
                style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "1.5rem", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.1)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                <div style={{ display: "flex", gap: 8, marginBottom: "0.75rem", flexWrap: "wrap" }}>
                  {post.category && <span style={{ background: "#fff7ed", color: "#c2410c", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, textTransform: "capitalize" }}>{post.category.replace(/-/g, " ")}</span>}
                  {post.state && <span style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4 }}>{post.state}</span>}
                </div>
                <h2 style={{ fontSize: 17, fontWeight: 700, color: "#111", margin: "0 0 8px", lineHeight: 1.4 }}>{post.title}</h2>
                {post.excerpt && <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 1rem", lineHeight: 1.6 }}>{post.excerpt}</p>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#9ca3af" }}>
                  <span>{post.author}</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

function BlogPostPage({ slug, setPage, user, onSignIn, onSignOut, onPostListing }) {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`${API_URL}/api/blog/${slug}`);
        if (!res.ok) { setNotFound(true); return; }
        const data = await res.json();
        setPost(data.post);
      } catch (err) { setNotFound(true); }
      finally { setLoading(false); }
    };
    fetchPost();
  }, [slug]);
  useSeo(post ? {
    title: `${post.title} — RentHound Blog`,
    description: post.excerpt || undefined,
    path: `/blog/${post.slug}`,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt || undefined,
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: { "@type": post.author ? "Person" : "Organization", name: post.author || "RentHound" },
      publisher: { "@type": "Organization", name: "RentHound", url: SITE_URL },
      mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
      image: SEO_DEFAULTS.image,
    },
  } : { path: `/blog/${slug}` });

  if (loading) return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ textAlign: "center", padding: "4rem 0", color: "#6b7280" }}>Loading...</div>
    </div>
  );

  if (notFound || !post) return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ textAlign: "center", padding: "4rem 0", color: "#6b7280" }}>
        <IconBadge name="frown" color="#6b7280" background="#f3f4f6" />
        <p style={{ fontSize: 16 }}>Post not found.</p>
        <button onClick={() => { window.history.pushState({}, "", "/blog"); setPage("blog"); }} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer", marginTop: "1rem" }}>← Back to Blog</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "2rem 1.5rem 5rem" }}>
        <button onClick={() => { window.history.pushState({}, "", "/blog"); setPage("blog"); }}
          style={{ background: "none", border: "none", color: "#6b7280", fontSize: 14, cursor: "pointer", marginBottom: "1.5rem", padding: 0 }}>← Back to Blog</button>
        <div style={{ display: "flex", gap: 8, marginBottom: "1rem", flexWrap: "wrap" }}>
          {post.category && <span style={{ background: "#fff7ed", color: "#c2410c", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 4, textTransform: "capitalize" }}>{post.category.replace(/-/g, " ")}</span>}
          {post.state && <span style={{ background: "#eff6ff", color: "#1d4ed8", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 4 }}>{post.state}</span>}
          {post.city && <span style={{ background: "#f0fdf4", color: "#166534", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 4 }}>{post.city}</span>}
        </div>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: "#111", margin: "0 0 1rem", lineHeight: 1.3 }}>{post.title}</h1>
        <div style={{ display: "flex", gap: 16, fontSize: 13, color: "#9ca3af", marginBottom: "2rem", borderBottom: "1px solid #e5e7eb", paddingBottom: "1rem" }}>
          <span>By {post.author}</span>
          <span>{new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
        </div>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem", lineHeight: 1.9, fontSize: 16, color: "#374151" }}
          dangerouslySetInnerHTML={{ __html: parseMarkdown(post.content || "") }} />
        <div style={{ background: "#fff7ed", borderRadius: 12, border: "1px solid #fed7aa", padding: "1.5rem", marginTop: "2rem" }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: "#c2410c", margin: "0 0 8px", display: "flex", alignItems: "center", gap: 8 }}><Icon name="home" size={18} /> Looking for a rental?</h3>
          <p style={{ fontSize: 14, color: "#92400e", margin: "0 0 1rem" }}>Search thousands of apartments and houses for rent near you.</p>
          <button onClick={() => { window.history.pushState({}, "", "/"); setPage("home"); }} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Search Rentals →</button>
        </div>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

// ─── Post Listing Modal ────────────────────────────────────────────────────────

function PostListingModal({ user, onClose, onSignIn }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    type: "apartment", title: "", location: "", price: "",
    beds: "", baths: "", sqft: "", description: "",
    contact_name: "", contact_email: "", contact_phone: "",
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const EMAIL_RE = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
  const stepOneError =
    form.title.trim().length < 3 ? "Add a listing title (at least 3 characters)." :
    form.location.trim().length < 3 ? "Add a location, e.g. Philadelphia, PA." :
    !(Number(form.price) >= 1) ? "Add the monthly rent." :
    form.contact_name.trim().length < 2 ? "Add your name." :
    !EMAIL_RE.test(form.contact_email.trim()) ? "Add a valid email address so renters can reach you." :
    null;
  const [showStepOneError, setShowStepOneError] = useState(false);

  const isDirty = photos.length > 0 || Object.entries(form).some(([k, v]) => k !== "type" && v);
  const handleClose = () => {
    if (loading) return;
    if (step < 4 && isDirty && !window.confirm("Discard this listing? What you've entered won't be saved.")) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const maxSize = 1200;
          let w = img.width, h = img.height;
          if (w > maxSize || h > maxSize) {
            if (w > h) { h = (h / w) * maxSize; w = maxSize; }
            else { w = (w / h) * maxSize; h = maxSize; }
          }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.75);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files).slice(0, 10 - photos.length);
    const previews = files.map(f => ({ file: f, url: URL.createObjectURL(f) }));
    setPhotos(prev => [...prev, ...previews].slice(0, 10));
  };

  const removePhoto = (i) => setPhotos(prev => prev.filter((_, idx) => idx !== i));

  const uploadPhotos = async () => {
    setUploading(true);
    const urls = [];
    for (const photo of photos) {
      const compressed = await compressImage(photo.file);
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;
      const { error } = await supabase.storage.from("listing-photos").upload(fileName, compressed, { contentType: "image/jpeg" });
      if (!error) {
        const { data: urlData } = supabase.storage.from("listing-photos").getPublicUrl(fileName);
        urls.push(urlData.publicUrl);
      }
    }
    setUploading(false);
    return urls;
  };

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true); setPromoError(null); setPromoApplied(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/checkout/validate-promo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ code: promoCode }),
      });
      const data = await res.json();
      if (data.valid) setPromoApplied(true);
      else setPromoError(data.error || "That promo code isn't valid.");
    } catch { setPromoError("Could not check the code. Please try again."); }
    finally { setPromoChecking(false); }
  };

  const handleSubmit = async () => {
    setLoading(true); setError(null);
    try {
      const photoUrls = await uploadPhotos();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ promoCode, listingData: { ...form, photos: photoUrls } }),
      });
      const data = await res.json();
      if (data.free) { setPromoApplied(true); setStep(4); }
      else if (data.url) { window.location.href = data.url; }
      else setError(res.status === 400 && data.error ? data.error : "Something went wrong. Please try again.");
    } catch (err) { setError("Could not connect to server."); }
    finally { setLoading(false); }
  };

  if (!user) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 400, margin: "0 1rem", textAlign: "center" }}>
          <IconBadge name="lock" />
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111", margin: "0 0 0.5rem" }}>Sign in to post a listing</h2>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.5rem" }}>You need to be signed in to post a rental listing on RentHound.</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px", fontSize: 14, cursor: "pointer", color: "#374151" }}>Cancel</button>
            <button onClick={() => { onClose(); onSignIn(); }} style={{ flex: 1, background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Sign in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) handleClose(); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", padding: "1rem", boxSizing: "border-box" }}>
      <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 520, margin: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#111" }}>Post a listing</h2>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>Step {step} of 4</p>
          </div>
          <button onClick={handleClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 4 }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ height: 4, background: "#f3f4f6", borderRadius: 4, marginBottom: "1.5rem" }}>
          <div style={{ height: 4, background: "#F97316", borderRadius: 4, width: `${(step / 4) * 100}%`, transition: "width 0.3s" }} />
        </div>
        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Property type</label>
              <select value={form.type} onChange={e => update("type", e.target.value)} style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", background: "white" }}>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="storage">Storage unit</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Listing title</label>
              <input value={form.title} onChange={e => update("title", e.target.value)} placeholder="e.g. Modern 2BR in Center City"
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Location</label>
              <input value={form.location} onChange={e => update("location", e.target.value)} placeholder="e.g. Philadelphia, PA"
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Monthly rent ($)</label>
              <input value={form.price} onChange={e => update("price", e.target.value)} placeholder="e.g. 1500" type="number"
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              {[["beds","Beds","e.g. 2"],["baths","Baths","e.g. 1"],["sqft","Sq ft","e.g. 850"]].map(([k,l,p]) => (
                <div key={k}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>{l}</label>
                  <input value={form[k]} onChange={e => update(k, e.target.value)} placeholder={p} type="number"
                    style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
                </div>
              ))}
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Description</label>
              <textarea value={form.description} onChange={e => update("description", e.target.value)}
                placeholder="Describe your property — features, amenities, nearby transit, pet policy, parking, etc."
                rows={4} style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }} />
            </div>
            <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 12, marginTop: 4 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 10px" }}>Your contact info (private — only used to forward inquiries)</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input value={form.contact_name} onChange={e => update("contact_name", e.target.value)} placeholder="Your name *"
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
                <input value={form.contact_email} onChange={e => update("contact_email", e.target.value)} placeholder="Your email address *" type="email"
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
                <input value={form.contact_phone} onChange={e => update("contact_phone", e.target.value)} placeholder="Your phone number (optional)"
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button onClick={handleClose} style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer", color: "#374151", fontWeight: 500 }}>Cancel</button>
            <button onClick={() => { if (stepOneError) setShowStepOneError(true); else setStep(2); }}
              style={{ flex: 2, background: stepOneError ? "#e5e7eb" : "#F97316", color: stepOneError ? "#6b7280" : "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Next: Add Photos →
            </button>
            </div>
            {showStepOneError && stepOneError && <p style={{ color: "#991b1b", fontSize: 13, margin: 0 }}>{stepOneError}</p>}
          </div>
        )}
        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Photos (up to 10)</label>
              <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 12px" }}>Photos are compressed automatically. You can skip this step.</p>
              {photos.length < 10 && (
                <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: "2px dashed #e5e7eb", borderRadius: 8, padding: "1.5rem", cursor: "pointer", color: "#6b7280", fontSize: 14, marginBottom: 12 }}>
                  <Icon name="camera" size={24} />
                  <span>Click to add photos ({photos.length}/10)</span>
                  <input type="file" accept="image/*" multiple onChange={handlePhotoChange} style={{ display: "none" }} />
                </label>
              )}
              {photos.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 12 }}>
                  {photos.map((p, i) => (
                    <div key={i} style={{ position: "relative", borderRadius: 8, overflow: "hidden", aspectRatio: "1" }}>
                      <img src={p.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button onClick={() => removePhoto(i)} style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="x" size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(1)} style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer", color: "#374151", fontWeight: 500 }}>← Back</button>
              <button onClick={() => setStep(3)} style={{ flex: 2, background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                {uploading ? "Uploading..." : "Next: Payment →"}
              </button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: "#f8f7f4", borderRadius: 12, padding: "1.25rem" }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: "#111" }}>{form.title}</h3>
              <p style={{ margin: "0 0 8px", fontSize: 13, color: "#6b7280" }}><Icon name="pin" size={13} style={{ marginRight: 4, marginTop: -2 }} />{form.location}</p>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111" }}>${Number(form.price).toLocaleString()}/mo</p>
            </div>
            <div style={{ border: "2px solid #F97316", borderRadius: 12, padding: "1.25rem", textAlign: "center" }}>
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "#6b7280" }}>Listing fee</p>
              <p style={{ margin: "0 0 4px", fontSize: 32, fontWeight: 700, color: "#111" }}>$5<span style={{ fontSize: 16, fontWeight: 400, color: "#6b7280" }}>/month</span></p>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>Unlimited listings · Cancel anytime</p>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Have a promo code?</label>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={promoCode} onChange={e => { setPromoCode(e.target.value); setPromoApplied(false); setPromoError(null); }} onKeyDown={e => { if (e.key === "Enter") applyPromo(); }} placeholder="Enter code"
                  style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111" }} />
                <button onClick={applyPromo} disabled={promoChecking} style={{ background: "#0f1f3d", color: "white", border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 14, cursor: promoChecking ? "default" : "pointer", fontWeight: 500, opacity: promoChecking ? 0.6 : 1 }}>{promoChecking ? "Checking..." : "Apply"}</button>
              </div>
              {promoApplied && promoCode && <p style={{ color: "#065f46", fontSize: 13, margin: "6px 0 0", display: "flex", alignItems: "center", gap: 6 }}><Icon name="check" size={14} /> Promo code applied — listing is free!</p>}
              {promoError && <p style={{ color: "#991b1b", fontSize: 13, margin: "6px 0 0" }}>{promoError}</p>}
            </div>
            {error && <p style={{ color: "#991b1b", fontSize: 13, margin: 0 }}>{error}</p>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setStep(2)} style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer", color: "#374151", fontWeight: 500 }}>← Back</button>
              <button onClick={handleSubmit} disabled={loading}
                style={{ flex: 2, background: loading ? "#e5e7eb" : "#F97316", color: loading ? "#9ca3af" : "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
                {loading ? "Processing..." : promoApplied && promoCode ? "Post for free →" : "Pay $5 & post →"}
              </button>
            </div>
          </div>
        )}
        {step === 4 && (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <IconBadge name="party" />
            <h3 style={{ fontSize: 20, fontWeight: 700, color: "#111", margin: "0 0 0.5rem" }}>Listing submitted!</h3>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.5rem" }}>Your listing is now live on RentHound.</p>
            <button onClick={onClose} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "12px 24px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Line icons drawn inline (shapes from the Lucide set, ISC license), so there's no icon package to load.
const ICON_PATHS = {
  pin: <><path d="M20 10c0 4.99-5.54 10.19-7.4 11.8a1 1 0 0 1-1.2 0C9.54 20.19 4 14.99 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" /></>,
  wallet: <><path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" /><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" /></>,
  home: <><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>,
  search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
  x: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
  menu: <><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h16" /></>,
  heart: <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />,
  camera: <><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" /><circle cx="12" cy="13" r="3" /></>,
  mail: <><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  checkCircle: <><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>,
  lock: <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  file: <><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" /><path d="M14 2v4a2 2 0 0 0 2 2h4" /><path d="M16 13H8" /><path d="M16 17H8" /></>,
  chart: <><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></>,
  pencil: <><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" /><path d="m15 5 4 4" /></>,
  ban: <><circle cx="12" cy="12" r="10" /><path d="m4.9 4.9 14.2 14.2" /></>,
  party: <><path d="M5.8 11.3 2 22l10.7-3.79" /><path d="M4 3h.01" /><path d="M22 8h.01" /><path d="M15 2h.01" /><path d="M22 20h.01" /><path d="m22 2-2.24.75a2.9 2.9 0 0 0-1.96 3.12c.1.86-.57 1.63-1.45 1.63h-.38c-.86 0-1.6.6-1.76 1.44L14 10" /><path d="m22 13-.82-.33c-.86-.34-1.82.2-1.98 1.11c-.11.7-.72 1.22-1.43 1.22H17" /><path d="m11 2 .33.82c.34.86-.2 1.82-1.11 1.98C9.52 4.9 9 5.52 9 6.23V7" /><path d="M11 13c1.93 1.93 2.83 4.17 2 5-.83.83-3.07-.07-5-2-1.93-1.93-2.83-4.17-2-5 .83-.83 3.07.07 5 2Z" /></>,
  frown: <><circle cx="12" cy="12" r="10" /><path d="M16 16s-1.5-2-4-2-4 2-4 2" /><path d="M9 9h.01" /><path d="M15 9h.01" /></>,
  bed: <><path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v9" /></>,
  bath: <><path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" /><path d="M10 5 8 7" /><path d="M2 12h20" /><path d="M7 19v2" /><path d="M17 19v2" /></>,
  ruler: <><path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.41 2.41 0 0 1 0-3.4l2.6-2.6a2.41 2.41 0 0 1 3.4 0Z" /><path d="m14.5 12.5 2-2" /><path d="m11.5 9.5 2-2" /><path d="m8.5 6.5 2-2" /><path d="m17.5 15.5 2-2" /></>,
  share: <><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="m16 6-4-4-4 4" /><path d="M12 2v13" /></>,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></>,
  image: <><rect width="18" height="18" x="3" y="3" rx="2" ry="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" /></>,
  arrowLeft: <><path d="m12 19-7-7 7-7" /><path d="M19 12H5" /></>,
};

function Icon({ name, size = 16, color = "currentColor", fill = "none", strokeWidth = 2, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0, display: "inline-block", verticalAlign: "middle", ...style }}>
      {ICON_PATHS[name]}
    </svg>
  );
}

// The round icon badge used at the top of empty states and confirmation screens.
function IconBadge({ name, color = "#F97316", background = "#fff7ed" }) {
  return (
    <div style={{ width: 56, height: 56, borderRadius: "50%", background, color, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
      <Icon name={name} size={26} />
    </div>
  );
}

const ListingSkeletons = ({ count = 6 }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }} aria-label="Loading listings" role="status">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} style={{ background: "white", borderRadius: 14, border: "1px solid #ebe8e2", overflow: "hidden" }}>
        <div className="rh-skeleton" style={{ height: 190, borderRadius: 0 }} />
        <div style={{ padding: "1rem" }}>
          <div className="rh-skeleton" style={{ height: 20, width: "40%", marginBottom: 10 }} />
          <div className="rh-skeleton" style={{ height: 14, width: "75%", marginBottom: 8 }} />
          <div className="rh-skeleton" style={{ height: 12, width: "50%", marginBottom: 16 }} />
          <div className="rh-skeleton" style={{ height: 12, width: "60%" }} />
        </div>
      </div>
    ))}
  </div>
);


function EditListingModal({ listing, onClose, onSaved, session }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const rawId = listing.id.replace("user_", "");
  const [form, setForm] = useState({
    type: listing.type || "apartment", title: listing.title || "", location: listing.location || "",
    price: listing.price || "", beds: listing.beds ?? "", baths: listing.baths ?? "", sqft: listing.sqft ?? "",
    description: listing.description || "",
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/listings/${rawId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.listing) { onSaved(data.listing); onClose(); }
      else setError("Could not save changes. Please try again.");
    } catch (err) { setError("Could not connect to server."); }
    finally { setLoading(false); }
  };
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, overflowY: "auto", padding: "1rem", boxSizing: "border-box" }}>
      <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 520, margin: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>Edit listing</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 4 }}><Icon name="x" size={20} /></button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[["type","Property type","select"],["title","Listing title","text"],["location","Location","text"],["price","Monthly rent ($)","number"]].map(([k,l,t]) => (
            <div key={k}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>{l}</label>
              {t === "select" ? (
                <select value={form[k]} onChange={e => update(k, e.target.value)} style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", background: "white" }}>
                  <option value="apartment">Apartment</option><option value="house">House</option><option value="storage">Storage unit</option>
                </select>
              ) : (
                <input value={form[k]} onChange={e => update(k, e.target.value)} type={t}
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
              )}
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            {[["beds","Beds"],["baths","Baths"],["sqft","Sq ft"]].map(([k,l]) => (
              <div key={k}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>{l}</label>
                <input value={form[k]} onChange={e => update(k, e.target.value)} type="number"
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
              </div>
            ))}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Description</label>
            <textarea value={form.description} onChange={e => update("description", e.target.value)}
              placeholder="Describe your property — features, amenities, nearby transit, pet policy, parking, etc."
              rows={4} style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box", resize: "vertical", lineHeight: 1.6 }} />
          </div>
          {error && <p style={{ color: "#991b1b", fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
            <button onClick={onClose} style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer", color: "#374151", fontWeight: 500 }}>Cancel</button>
            <button onClick={handleSave} disabled={loading}
              style={{ flex: 2, background: loading ? "#e5e7eb" : "#F97316", color: loading ? "#9ca3af" : "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: loading ? "default" : "pointer" }}>
              {loading ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactModal({ listing, onClose }) {
  const [name, setName] = useState(""); const [email, setEmail] = useState(""); const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false); const [sent, setSent] = useState(false); const [error, setError] = useState(null);
  const handleSubmit = async () => {
    if (!name || !email || !message) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/listings/${listing.id}/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message }),
      });
      const data = await res.json();
      if (data.success) setSent(true);
      else setError(res.status === 400 && data.error ? data.error : "Could not send message. Please try again.");
    } catch (err) { setError("Could not connect to server."); }
    finally { setLoading(false); }
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 440, margin: "0 1rem", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>Contact Landlord</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 4 }}><Icon name="x" size={20} /></button>
        </div>
        {sent ? (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <IconBadge name="checkCircle" color="#059669" background="#d1fae5" />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 0.5rem" }}>Message sent!</h3>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.5rem" }}>The landlord will get back to you at {email}.</p>
            <button onClick={onClose} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Done</button>
          </div>
        ) : (
          <>
            <div style={{ background: "#f8f7f4", borderRadius: 8, padding: "0.875rem 1rem", marginBottom: "1.25rem" }}>
              <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 14, color: "#111" }}>{listing.title}</p>
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}><Icon name="pin" size={13} style={{ marginRight: 4, marginTop: -2 }} />{listing.location}</p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: "1rem" }}>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name"
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" type="email"
                style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Hi, I'm interested in this listing. Is it still available?"
                rows={4} style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box", resize: "vertical" }} />
            </div>
            {error && <p style={{ color: "#991b1b", fontSize: 13, margin: "0 0 1rem" }}>{error}</p>}
            <button onClick={handleSubmit} disabled={loading || !name || !email || !message}
              style={{ width: "100%", background: loading || !name || !email || !message ? "#e5e7eb" : "#F97316", color: loading || !name || !email || !message ? "#9ca3af" : "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: loading || !name || !email || !message ? "default" : "pointer" }}>
              {loading ? "Sending..." : "Send message →"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SignInModal({ onClose }) {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false); const [error, setError] = useState(null);
  const handleSubmit = async () => {
    if (!email) return; setLoading(true); setError(null);
    const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: "https://www.renthound.com" } });
    if (error) setError(error.message); else setSent(true);
    setLoading(false);
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 400, margin: "0 1rem", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>Sign in to RentHound</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 4 }}><Icon name="x" size={20} /></button>
        </div>
        {sent ? (
          <div style={{ textAlign: "center", padding: "1rem 0" }}>
            <IconBadge name="mail" />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 0.5rem" }}>Check your email!</h3>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.5rem" }}>We sent a magic link to <strong>{email}</strong>.</p>
            <button onClick={onClose} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Got it</button>
          </div>
        ) : (
          <>
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.5rem" }}>Enter your email and we'll send you a magic link — no password needed.</p>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSubmit()} placeholder="you@example.com"
              style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 15, outline: "none", color: "#111", boxSizing: "border-box", marginBottom: "1rem" }} />
            {error && <p style={{ color: "#991b1b", fontSize: 13, margin: "0 0 1rem" }}>{error}</p>}
            <button onClick={handleSubmit} disabled={loading || !email}
              style={{ width: "100%", background: loading || !email ? "#e5e7eb" : "#F97316", color: loading || !email ? "#9ca3af" : "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: loading || !email ? "default" : "pointer" }}>
              {loading ? "Sending..." : "Send magic link"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function PricingPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  useSeo({ title: "Pricing — RentHound", description: "List your rental on RentHound for just $5/month. Unlimited listings, photo uploads, inquiry forwarding, and analytics. Cancel anytime.", path: "/pricing" });

  const features = [
    { icon: "home", title: "Unlimited listings", desc: "Post as many rental listings as you have. No per-listing fees." },
    { icon: "camera", title: "Photo uploads", desc: "Upload up to 10 photos per listing. Auto-compressed for fast loading." },
    { icon: "mail", title: "Inquiry forwarding", desc: "Renters contact you directly through RentHound. Your email stays private." },
    { icon: "chart", title: "Listing analytics", desc: "See how many people are viewing each of your listings." },
    { icon: "pencil", title: "Edit anytime", desc: "Update your listing details, price, or photos whenever you need." },
    { icon: "ban", title: "Cancel anytime", desc: "No long-term commitment. Cancel your subscription with one click." },
  ];

  const faqs = [
    { q: "How does the $5/month work?", a: "It's a simple monthly subscription. You pay $5/month and can post unlimited rental listings. Your listings stay active as long as your subscription is active." },
    { q: "Can I cancel anytime?", a: "Yes! You can cancel your subscription at any time from your dashboard. Your listings will remain active until the end of your billing period." },
    { q: "How do renters contact me?", a: "Renters fill out a contact form on your listing and the message gets forwarded to your email address. Your personal email stays private — renters never see it." },
    { q: "How is RentHound different from Zillow or Craigslist?", a: "RentHound aggregates listings from Redfin, Zillow, and independent landlords into one place. When you post here, your listing stands out because it's shown alongside aggregated results — and renters can contact you directly." },
    { q: "Do I need a credit card to try it?", a: "If you have a promo code, you can post for free with no credit card required. Otherwise, you'll need a card for the $5/month subscription." },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ background: "linear-gradient(135deg, #0f1f3d 0%, #1a3566 100%)", padding: "4rem 2rem", textAlign: "center" }}>
        <h1 style={{ color: "white", fontSize: 40, fontWeight: 700, margin: "0 0 1rem", letterSpacing: "-0.5px" }}>Simple, transparent pricing</h1>
        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 18, maxWidth: 520, margin: "0 auto" }}>One flat monthly fee. Unlimited listings. No hidden costs.</p>
      </div>
      <div style={{ maxWidth: 480, margin: "-2rem auto 0", padding: "0 1.5rem" }}>
        <div style={{ background: "white", borderRadius: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.15)", padding: "2.5rem", textAlign: "center", border: "2px solid #F97316" }}>
          <div style={{ display: "inline-block", background: "#fff7ed", color: "#c2410c", fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 20, marginBottom: "1rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Landlord Plan</div>
          <div style={{ fontSize: 64, fontWeight: 800, color: "#111", lineHeight: 1, margin: "0 0 4px" }}>$5</div>
          <div style={{ fontSize: 16, color: "#6b7280", margin: "0 0 1.5rem" }}>per month</div>
          <div style={{ borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6", padding: "1.25rem 0", margin: "0 0 1.5rem" }}>
            {["Unlimited rental listings", "Up to 10 photos per listing", "Direct inquiry forwarding", "Listing view analytics", "Edit or delete anytime", "Cancel anytime"].map(f => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", textAlign: "left" }}>
                <Icon name="check" size={18} color="#F97316" strokeWidth={2.5} />
                <span style={{ fontSize: 15, color: "#374151" }}>{f}</span>
              </div>
            ))}
          </div>
          <button onClick={onPostListing} style={{ width: "100%", background: "#F97316", color: "white", border: "none", borderRadius: 10, padding: "14px", fontSize: 16, fontWeight: 700, cursor: "pointer" }}>Post your first listing →</button>
          <p style={{ fontSize: 13, color: "#9ca3af", margin: "1rem 0 0" }}>No commitment · Cancel anytime</p>
        </div>
      </div>
      <div style={{ maxWidth: 900, margin: "4rem auto", padding: "0 1.5rem" }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: "#111", textAlign: "center", margin: "0 0 2rem" }}>Everything you need to rent faster</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1.25rem" }}>
          {features.map(f => (
            <div key={f.title} style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "1.5rem" }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: "#fff7ed", color: "#F97316", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "0.75rem" }}><Icon name={f.icon} size={22} /></div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111", margin: "0 0 6px" }}>{f.title}</h3>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: "white", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: "#111", margin: "0 0 2.5rem" }}>How it works</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "2rem" }}>
            {[{ step: "1", title: "Create your listing", desc: "Fill in your rental details, add photos, and set your price." }, { step: "2", title: "Pay $5/month", desc: "Subscribe with a card or use a promo code to post for free." }, { step: "3", title: "Get inquiries", desc: "Renters find your listing and contact you directly via email." }].map(s => (
              <div key={s.step} style={{ textAlign: "center" }}>
                <div style={{ width: 48, height: 48, background: "#F97316", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem", fontSize: 20, fontWeight: 800, color: "white" }}>{s.step}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111", margin: "0 0 6px" }}>{s.title}</h3>
                <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 700, margin: "4rem auto", padding: "0 1.5rem" }}>
        <h2 style={{ fontSize: 28, fontWeight: 700, color: "#111", textAlign: "center", margin: "0 0 2rem" }}>Frequently asked questions</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {faqs.map(f => (
            <div key={f.q} style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "1.25rem 1.5rem" }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>{f.q}</h3>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.7 }}>{f.a}</p>
            </div>
          ))}
        </div>
      </div>
      <div style={{ background: "linear-gradient(135deg, #0f1f3d 0%, #1a3566 100%)", padding: "4rem 2rem", textAlign: "center" }}>
        <h2 style={{ color: "white", fontSize: 32, fontWeight: 700, margin: "0 0 1rem" }}>Ready to list your rental?</h2>
        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 16, margin: "0 0 2rem" }}>Join landlords already using RentHound to find great tenants.</p>
        <button onClick={onPostListing} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 10, padding: "14px 32px", fontSize: 16, fontWeight: 700, cursor: "pointer" }}>Post a listing for $5/month →</button>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

// ─── Terms of Service Page ─────────────────────────────────────────────────────

function TermsPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  useSeo({ title: "Terms of Service — RentHound", description: "The terms for using RentHound to search rentals and post rental listings.", path: "/terms" });

  const sections = [
    { title: "Agreement to Terms", content: "By accessing or using RentHound at renthound.com (\"the Site\"), you agree to be bound by these Terms of Service (\"Terms\"). If you do not agree to these Terms, please do not use the Site. These Terms apply to all visitors, users, and anyone else who accesses or uses RentHound." },
    { title: "About RentHound", content: "RentHound is a rental listing aggregator and marketplace that displays rental listings sourced from third-party platforms as well as listings posted directly by users. RentHound does not own, manage, or control any of the rental properties listed on the Site and is not a party to any rental agreement between landlords and tenants." },
    { title: "Eligibility", content: "You must be at least 18 years of age to use RentHound. By using the Site you represent and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms." },
    { title: "User Accounts", content: "RentHound uses passwordless authentication via magic link emails. By creating an account you agree to: provide accurate and complete information; keep your email address current and valid; be responsible for all activity that occurs under your account; and notify us immediately if you believe your account has been compromised. You may not create an account on behalf of another person without their permission. RentHound reserves the right to suspend or terminate accounts that violate these Terms." },
    { title: "Listing Policies", content: "Users may post rental listings on RentHound for a fee. By posting a listing you agree that: you are the legal owner of the property or are authorized to list it for rent; all information in your listing is accurate, complete, and not misleading; your listing does not violate any applicable laws or regulations; you will promptly update or remove your listing if the property is no longer available; you will not post duplicate listings for the same property; and you will not post listings for properties you do not have the legal right to rent. Some listings on RentHound are sourced from third-party platforms including Redfin and Zillow. RentHound does not guarantee the accuracy, completeness, or availability of these listings. RentHound reserves the right to remove any listing that violates these Terms." },
    { title: "Fees and Payment", content: "Posting a rental listing on RentHound requires payment of a recurring monthly subscription fee. By subscribing you authorize RentHound to charge your payment method on a recurring monthly basis until you cancel." },
    { title: "No Refund Policy", content: "All fees paid to RentHound are non-refundable. This includes but is not limited to listing fees, subscription fees, and any other charges. If you cancel your subscription your listing will remain active until the end of the current billing period and will not be renewed. No partial refunds will be issued for unused portions of a billing period." },
    { title: "Cancellation", content: "You may cancel your listing subscription at any time through your account dashboard. Cancellation takes effect at the end of the current billing period. RentHound reserves the right to modify pricing with reasonable advance notice to subscribers." },
    { title: "Prohibited Conduct", content: "By using RentHound you agree not to: post false, fraudulent, or misleading listings or information; impersonate any person or entity; use the Site for any unlawful purpose; attempt to gain unauthorized access to any part of the Site; scrape, crawl, or harvest data from the Site without permission; interfere with or disrupt the Site or its servers; use the Site to send spam or unsolicited communications; or discriminate in rental listings based on race, color, religion, sex, national origin, disability, familial status, or any other protected class under applicable law." },
    { title: "Fair Housing", content: "RentHound is committed to fair housing. All listings posted on the Site must comply with the Fair Housing Act and all applicable federal, state, and local fair housing laws. Discriminatory listings will be removed immediately and the associated account may be permanently banned." },
    { title: "Intellectual Property", content: "All content on RentHound including but not limited to text, graphics, logos, and software is the property of RentHound or its content suppliers and is protected by applicable intellectual property laws. User-posted listing content remains the property of the user. By posting content on RentHound you grant RentHound a non-exclusive, royalty-free, worldwide license to display, distribute, and promote that content in connection with operating the Site." },
    { title: "Disclaimer of Warranties", content: "RentHound is provided on an \"as is\" and \"as available\" basis without warranties of any kind, either express or implied. RentHound does not warrant that the Site will be uninterrupted or error-free, that any listing information is accurate or current, or that the Site is free of viruses or other harmful components." },
    { title: "Limitation of Liability", content: "To the fullest extent permitted by applicable law, RentHound and its owners, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Site or any rental transaction facilitated through the Site. RentHound's total liability to you for any claim shall not exceed the total amount you have paid to RentHound in the twelve months preceding the claim." },
    { title: "Indemnification", content: "You agree to indemnify and hold harmless RentHound and its owners, employees, and agents from any claims, damages, losses, or expenses including reasonable legal fees arising from your use of the Site, your violation of these Terms, or your violation of any rights of a third party." },
    { title: "Governing Law", content: "These Terms shall be governed by and construed in accordance with the laws of the United States and the Commonwealth of Pennsylvania, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Site shall be resolved in the courts of Pennsylvania." },
    { title: "Changes to These Terms", content: "RentHound reserves the right to modify these Terms at any time. We will notify users of significant changes by posting a notice on the Site or sending an email to registered users. Your continued use of the Site after changes are posted constitutes your acceptance of the updated Terms." },
    { title: "Contact Us", content: "If you have any questions about these Terms of Service please contact us at support@renthound.com." },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav onBack={() => setPage("home")} user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "3rem 1.5rem 5rem" }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: "#111", marginBottom: "0.5rem" }}>Terms of Service</h1>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: "2rem" }}>Effective date: May 1, 2026 · Last updated: May 1, 2026</p>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem", lineHeight: 1.8, color: "#374151", fontSize: 15 }}>
          {sections.map((s, i) => (
            <div key={i} style={{ marginBottom: "1.75rem" }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111", margin: "0 0 0.5rem" }}>{s.title}</h2>
              <p style={{ margin: 0, lineHeight: 1.8 }}>{s.content}</p>
            </div>
          ))}
        </div>
        <div style={{ background: "#fff7ed", borderRadius: 12, border: "1px solid #fed7aa", padding: "1.25rem 1.5rem", marginTop: "1.5rem" }}>
          <p style={{ margin: 0, fontSize: 14, color: "#92400e" }}>Questions about these terms? Email us at <a href="mailto:support@renthound.com" style={{ color: "#c2410c", fontWeight: 600 }}>support@renthound.com</a></p>
        </div>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

function AdminPage({ setPage }) {
  const [adminSecret, setAdminSecret] = useState(""); const [authed, setAuthed] = useState(false);
  const [listings, setListings] = useState([]); const [reports, setReports] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]); const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false); const [activeTab, setActiveTab] = useState("listings");
  const [error, setError] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [showNewPost, setShowNewPost] = useState(false);
  const [postForm, setPostForm] = useState({ slug: "", title: "", excerpt: "", content: "", category: "tenant-rights", state: "", city: "", author: "RentHound Team", published: false });

  const fetchData = async (secret) => {
    setLoading(true); setError(null);
    try {
      const headers = { "x-admin-secret": secret };
      const [lr, rr, br, ur] = await Promise.all([
        fetch(`${API_URL}/api/admin/listings`, { headers }),
        fetch(`${API_URL}/api/admin/reports`, { headers }),
        fetch(`${API_URL}/api/blog/admin/all`, { headers }),
        fetch(`${API_URL}/api/admin/users`, { headers }),
      ]);
      if (!lr.ok) throw new Error("Invalid");
      setListings((await lr.json()).listings || []);
      setReports((await rr.json()).reports || []);
      setBlogPosts((await br.json()).posts || []);
      setUsers(ur.ok ? (await ur.json()).users || [] : []);
      setAuthed(true);
    } catch { setError("Invalid admin secret or could not connect."); }
    finally { setLoading(false); }
  };

  const handleDeleteListing = async (id) => {
    if (!window.confirm("Delete this listing permanently?")) return;
    const res = await fetch(`${API_URL}/api/admin/listings/${id}`, { method: "DELETE", headers: { "x-admin-secret": adminSecret } });
    if ((await res.json()).success) setListings(prev => prev.filter(l => l.id !== id));
  };

  const handleDismissReport = async (id) => {
    const res = await fetch(`${API_URL}/api/admin/reports/${id}`, { method: "DELETE", headers: { "x-admin-secret": adminSecret } });
    if ((await res.json()).success) setReports(prev => prev.filter(r => r.id !== id));
  };

  const handleSavePost = async () => {
    const method = editingPost ? "PUT" : "POST";
    const url = editingPost ? `${API_URL}/api/blog/admin/${editingPost.id}` : `${API_URL}/api/blog/admin`;
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json", "x-admin-secret": adminSecret }, body: JSON.stringify(postForm) });
    const data = await res.json();
    if (data.post) {
      if (editingPost) setBlogPosts(prev => prev.map(p => p.id === editingPost.id ? data.post : p));
      else setBlogPosts(prev => [data.post, ...prev]);
      setEditingPost(null); setShowNewPost(false);
      setPostForm({ slug: "", title: "", excerpt: "", content: "", category: "tenant-rights", state: "", city: "", author: "RentHound Team", published: false });
    }
  };

  const handleDeletePost = async (id) => {
    if (!window.confirm("Delete this blog post permanently?")) return;
    const res = await fetch(`${API_URL}/api/blog/admin/${id}`, { method: "DELETE", headers: { "x-admin-secret": adminSecret } });
    if ((await res.json()).success) setBlogPosts(prev => prev.filter(p => p.id !== id));
  };

  const handleEditPost = (post) => {
    setPostForm({ slug: post.slug, title: post.title, excerpt: post.excerpt || "", content: post.content || "", category: post.category || "tenant-rights", state: post.state || "", city: post.city || "", author: post.author || "RentHound Team", published: post.published });
    setEditingPost(post); setShowNewPost(true);
  };

  if (!authed) return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 400, margin: "0 1rem", boxShadow: "0 4px 24px rgba(0,0,0,0.1)" }}>
        <h2 style={{ margin: "0 0 0.5rem", fontSize: 22, fontWeight: 700, color: "#111" }}>🔐 Admin Access</h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.5rem" }}>Enter your admin secret to continue.</p>
        <input type="password" value={adminSecret} onChange={e => setAdminSecret(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchData(adminSecret)} placeholder="Admin secret"
          style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box", marginBottom: "1rem" }} />
        {error && <p style={{ color: "#991b1b", fontSize: 13, margin: "0 0 1rem" }}>{error}</p>}
        <button onClick={() => fetchData(adminSecret)} disabled={loading || !adminSecret}
          style={{ width: "100%", background: !adminSecret ? "#e5e7eb" : "#0f1f3d", color: !adminSecret ? "#9ca3af" : "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: !adminSecret ? "default" : "pointer" }}>
          {loading ? "Loading..." : "Enter Dashboard"}
        </button>
        <button onClick={() => setPage("home")} style={{ width: "100%", background: "none", border: "none", color: "#6b7280", fontSize: 13, cursor: "pointer", marginTop: "1rem" }}>← Back to site</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      {showNewPost && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, overflowY: "auto" }}>
          <div style={{ background: "white", borderRadius: 16, padding: "2rem", width: "100%", maxWidth: 700, margin: "1rem", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111" }}>{editingPost ? "Edit Blog Post" : "New Blog Post"}</h2>
              <button onClick={() => { setShowNewPost(false); setEditingPost(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", display: "flex", padding: 4 }}><Icon name="x" size={20} /></button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[["title","Title","text"],["slug","Slug (URL)","text"],["excerpt","Excerpt (short summary)","text"]].map(([k,l,t]) => (
                <div key={k}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>{l}</label>
                  <input value={postForm[k]} onChange={e => setPostForm(f => ({ ...f, [k]: e.target.value }))} type={t}
                    style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
                </div>
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Category</label>
                  <select value={postForm.category} onChange={e => setPostForm(f => ({ ...f, category: e.target.value }))} style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", background: "white" }}>
                    <option value="tenant-rights">Tenant Rights</option>
                    <option value="landlord-tips">Landlord Tips</option>
                    <option value="city-guides">City Guides</option>
                    <option value="legal-resources">Legal Resources</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>State</label>
                  <input value={postForm.state} onChange={e => setPostForm(f => ({ ...f, state: e.target.value }))} placeholder="e.g. Pennsylvania"
                    style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>City</label>
                  <input value={postForm.city} onChange={e => setPostForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. Philadelphia"
                    style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box" }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 }}>Content</label>
                <textarea value={postForm.content} onChange={e => setPostForm(f => ({ ...f, content: e.target.value }))}
                  rows={16} placeholder="Write your blog post content here..."
                  style={{ width: "100%", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#111", boxSizing: "border-box", resize: "vertical", lineHeight: 1.7, fontFamily: "system-ui, sans-serif" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="published" checked={postForm.published} onChange={e => setPostForm(f => ({ ...f, published: e.target.checked }))} />
                <label htmlFor="published" style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>Published (visible to public)</label>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button onClick={() => { setShowNewPost(false); setEditingPost(null); }} style={{ flex: 1, background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer", color: "#374151", fontWeight: 500 }}>Cancel</button>
                <button onClick={handleSavePost} style={{ flex: 2, background: "#0f1f3d", color: "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
                  {editingPost ? "Save changes" : "Create post"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <nav style={{ background: "#0f1f3d", padding: "0 2rem", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56 }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: 18 }}><span>Rent</span><span style={{ color: "#F97316" }}>Hound</span><span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, fontWeight: 400, marginLeft: 8 }}>Admin</span></span>
        <button onClick={() => setPage("home")} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>← Back to site</button>
      </nav>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#111", margin: 0 }}>Admin Dashboard</h1>
          <div style={{ fontSize: 13, color: "#6b7280" }}>{listings.length} listings · <span style={{ color: reports.length > 0 ? "#991b1b" : "#6b7280", fontWeight: reports.length > 0 ? 700 : 400 }}>{reports.length} reports</span> · {blogPosts.length} blog posts · {users.length} users</div>
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem", flexWrap: "wrap" }}>
          <button onClick={() => setActiveTab("listings")} style={{ background: activeTab === "listings" ? "#0f1f3d" : "white", color: activeTab === "listings" ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>All Listings ({listings.length})</button>
          <button onClick={() => setActiveTab("reports")} style={{ background: activeTab === "reports" ? "#991b1b" : "white", color: activeTab === "reports" ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Reports {reports.length > 0 ? `(${reports.length} ⚠️)` : "(0)"}</button>
          <button onClick={() => setActiveTab("blog")} style={{ background: activeTab === "blog" ? "#0f1f3d" : "white", color: activeTab === "blog" ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Blog Posts ({blogPosts.length})</button>
          <button onClick={() => setActiveTab("users")} style={{ background: activeTab === "users" ? "#0f1f3d" : "white", color: activeTab === "users" ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Users ({users.length})</button>
        </div>

        {activeTab === "listings" && (
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f8f7f4" }}>{["Listing","Price","Type","Status","Posted","Actions"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: h === "Actions" ? "right" : "left", color: "#6b7280", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>{listings.map((l, i) => (
                <tr key={l.id} style={{ borderTop: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                  <td style={{ padding: "12px 16px" }}><p style={{ margin: "0 0 2px", fontWeight: 600, color: "#111" }}>{l.title}</p><p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}><Icon name="pin" size={13} style={{ marginRight: 4, marginTop: -2 }} />{l.location}</p></td>
                  <td style={{ padding: "12px 16px", color: "#111", fontWeight: 500 }}>{l.price ? `$${Number(l.price).toLocaleString()}/mo` : "—"}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ background: "#f3f4f6", color: "#374151", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4, textTransform: "capitalize" }}>{l.type}</span></td>
                  <td style={{ padding: "12px 16px" }}><span style={{ background: l.status === "active" ? "#d1fae5" : "#f3f4f6", color: l.status === "active" ? "#065f46" : "#374151", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4 }}>{l.status}</span></td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{new Date(l.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button onClick={() => window.open(`https://www.renthound.com/?listing=${l.id}`, "_blank")} style={{ background: "#eff6ff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#1d4ed8" }}>View</button>
                      <button onClick={() => handleDeleteListing(l.id)} style={{ background: "#fef2f2", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#991b1b" }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

        {activeTab === "users" && (
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflowX: "auto" }}>
            {users.length === 0 ? <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}><div style={{ fontSize: 40, marginBottom: "1rem" }}>👤</div><p style={{ fontSize: 16, margin: 0 }}>No users yet.</p></div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead><tr style={{ background: "#f8f7f4" }}>{["Email","Signed Up","Last Sign-in","Confirmed","Listings"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: "left", color: "#6b7280", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                <tbody>{users.map((u, i) => (
                  <tr key={u.id} style={{ borderTop: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                    <td style={{ padding: "12px 16px" }}><p style={{ margin: "0 0 2px", fontWeight: 600, color: "#111" }}>{u.email || "—"}</p><p style={{ margin: 0, fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>{u.id}</p></td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "Never"}</td>
                    <td style={{ padding: "12px 16px" }}><span style={{ background: u.confirmed ? "#d1fae5" : "#fef3c7", color: u.confirmed ? "#065f46" : "#92400e", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4 }}>{u.confirmed ? "Yes" : "Pending"}</span></td>
                    <td style={{ padding: "12px 16px", color: "#111", fontWeight: 500 }}>{u.listings}{u.listings > 0 && <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}> ({u.active_listings} active)</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "reports" && (
          <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
            {reports.length === 0 ? <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}><div style={{ fontSize: 40, marginBottom: "1rem" }}>✅</div><p style={{ fontSize: 16, margin: 0 }}>No reports!</p></div> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead><tr style={{ background: "#fef2f2" }}>{["Reported Listing","Listing ID","Reported At","Actions"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: h === "Actions" ? "right" : "left", color: "#991b1b", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                <tbody>{reports.map((r, i) => (
                  <tr key={r.id} style={{ borderTop: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                    <td style={{ padding: "12px 16px" }}>
                      <p style={{ margin: "0 0 2px", fontWeight: 600, color: "#111" }}>{r.listing_title || "Unknown"}</p>
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280", fontFamily: "monospace" }}>{r.listing_id}</td>
                    <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{new Date(r.reported_at).toLocaleString()}</td>
                    <td style={{ padding: "12px 16px", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button onClick={() => window.open(`https://www.renthound.com/?listing=${r.listing_id}`, "_blank")} style={{ background: "#eff6ff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#1d4ed8" }}>View</button>
                        <button onClick={() => handleDeleteListing(r.listing_id)} style={{ background: "#fef2f2", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#991b1b" }}>Delete Listing</button>
                        <button onClick={() => handleDismissReport(r.id)} style={{ background: "#f3f4f6", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151" }}>Dismiss</button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === "blog" && (
          <div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <button onClick={() => { setEditingPost(null); setPostForm({ slug: "", title: "", excerpt: "", content: "", category: "tenant-rights", state: "", city: "", author: "RentHound Team", published: false }); setShowNewPost(true); }}
                style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>+ New Blog Post</button>
            </div>
            <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
              {blogPosts.length === 0 ? <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}><IconBadge name="file" /><p style={{ fontSize: 16, margin: 0 }}>No blog posts yet. Create your first one!</p></div> : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead><tr style={{ background: "#f8f7f4" }}>{["Title","Category","State","Status","Date","Actions"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: h === "Actions" ? "right" : "left", color: "#6b7280", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
                  <tbody>{blogPosts.map((p, i) => (
                    <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                      <td style={{ padding: "12px 16px" }}><p style={{ margin: "0 0 2px", fontWeight: 600, color: "#111" }}>{p.title}</p><p style={{ margin: 0, fontSize: 12, color: "#6b7280", fontFamily: "monospace" }}>/blog/{p.slug}</p></td>
                      <td style={{ padding: "12px 16px" }}><span style={{ background: "#fff7ed", color: "#c2410c", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4, textTransform: "capitalize" }}>{(p.category || "").replace(/-/g, " ")}</span></td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>{p.state || "—"}</td>
                      <td style={{ padding: "12px 16px" }}><span style={{ background: p.published ? "#d1fae5" : "#f3f4f6", color: p.published ? "#065f46" : "#374151", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4 }}>{p.published ? "Published" : "Draft"}</span></td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{new Date(p.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button onClick={() => handleEditPost(p)} style={{ background: "#f3f4f6", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151" }}>Edit</button>
                          <button onClick={() => handleDeletePost(p.id)} style={{ background: "#fef2f2", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#991b1b" }}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Dashboard({ user, setPage, onSignOut, onPostListing }) {
  const [listings, setListings] = useState([]); const [loading, setLoading] = useState(true);
  const [editingListing, setEditingListing] = useState(null); const [deletingId, setDeletingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null); const [session, setSession] = useState(null);
  useEffect(() => { supabase.auth.getSession().then(({ data: { session } }) => setSession(session)); }, []);
  useEffect(() => {
    const fetch_ = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${API_URL}/api/listings/my-listings`, { headers: { Authorization: `Bearer ${session.access_token}` } });
        setListings((await res.json()).listings || []);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetch_();
  }, []);
  const formatEndDate = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const isBilling = (l) => l.stripe_subscription_id && l.status === "active" && !l.cancel_at;
  const handleDelete = async (l) => {
    const message = isBilling(l)
      ? "Delete this listing? This also cancels its $5/month subscription right away, and it can't be undone."
      : "Delete this listing? This cannot be undone.";
    if (!window.confirm(message)) return;
    setDeletingId(l.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/listings/${l.id.replace("user_", "")}`, { method: "DELETE", headers: { Authorization: `Bearer ${session.access_token}` } });
      if ((await res.json()).success) setListings(prev => prev.filter(x => x.id !== l.id));
      else window.alert("Could not delete this listing. Please try again.");
    } catch (err) { console.error(err); window.alert("Could not connect to the server."); } finally { setDeletingId(null); }
  };
  const handleCancelSubscription = async (id) => {
    if (!window.confirm("Cancel your subscription? You won't be charged again. Your listing stays live until the end of the month you've already paid for, then it's taken down.")) return;
    setCancellingId(id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_URL}/api/checkout/cancel-subscription`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` }, body: JSON.stringify({ listing_id: id }) });
      const data = await res.json();
      if (data.success) setListings(prev => prev.map(l => l.id === id ? { ...l, cancel_at: data.cancel_at || new Date().toISOString() } : l));
      else window.alert("Could not cancel the subscription. Please try again.");
    } catch (err) { console.error(err); window.alert("Could not connect to the server."); } finally { setCancellingId(null); }
  };
  const handleSaved = (u) => setListings(prev => prev.map(l => l.id === u.id ? { ...l, ...u } : l));

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      {editingListing && session && <EditListingModal listing={editingListing} session={session} onClose={() => setEditingListing(null)} onSaved={handleSaved} />}
      <nav style={{ background: "#0f1f3d", padding: "0 2rem", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => setPage("home")}>
          <LogoMark />
          <span style={{ color: "white", fontWeight: 700, fontSize: 18 }}><span>Rent</span><span style={{ color: "#F97316" }}>Hound</span></span>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>{user.email}</span>
          <button onClick={onSignOut} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>Sign out</button>
        </div>
      </nav>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
          <div><h1 style={{ fontSize: 28, fontWeight: 700, color: "#111", margin: "0 0 4px" }}>My Dashboard</h1><p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>Track your listing performance</p></div>
          <button onClick={onPostListing} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>+ Post a listing</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "1rem", marginBottom: "2rem" }}>
          {[{ label: "Total listings", value: listings.length }, { label: "Total views", value: listings.reduce((s, l) => s + (l.views || 0), 0) }, { label: "Active listings", value: listings.filter(l => l.status === "active").length }].map(s => (
            <div key={s.label} style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "1.25rem" }}>
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "#6b7280" }}>{s.label}</p>
              <p style={{ margin: 0, fontSize: 28, fontWeight: 700, color: "#111" }}>{s.value}</p>
            </div>
          ))}
        </div>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid #e5e7eb" }}><h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111" }}>Your Listings</h2></div>
          {loading ? <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}>Loading...</div>
          : listings.length === 0 ? <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}><p style={{ fontSize: 16, margin: "0 0 1rem" }}>No listings yet.</p><button onClick={onPostListing} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Post your first listing</button></div>
          : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead><tr style={{ background: "#f8f7f4" }}>{["Listing","Price","Status","Views","Actions"].map(h => <th key={h} style={{ padding: "10px 16px", textAlign: h === "Views" || h === "Actions" ? "right" : "left", color: "#6b7280", fontWeight: 600, fontSize: 12, textTransform: "uppercase" }}>{h}</th>)}</tr></thead>
              <tbody>{listings.map((l, i) => (
                <tr key={l.id} style={{ borderTop: "1px solid #f3f4f6", background: i % 2 === 0 ? "white" : "#fafafa" }}>
                  <td style={{ padding: "12px 16px" }}>
                    <p style={{ margin: "0 0 2px", fontWeight: 600, color: "#111" }}>{l.title}</p>
                    <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}><Icon name="pin" size={13} style={{ marginRight: 4, marginTop: -2 }} />{l.location}</p>
                  </td>
                  <td style={{ padding: "12px 16px", color: "#111", fontWeight: 500 }}>{l.price ? `$${l.price.toLocaleString()}/mo` : "—"}</td>
                  <td style={{ padding: "12px 16px" }}><span style={{ background: l.status === "active" ? "#d1fae5" : "#f3f4f6", color: l.status === "active" ? "#065f46" : "#374151", fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4 }}>{l.status || "active"}</span>
                    {l.cancel_at && l.status === "active" && <p style={{ margin: "4px 0 0", fontSize: 11, color: "#c2410c" }}>Ends {formatEndDate(l.cancel_at)}</p>}</td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}><span style={{ fontWeight: 700, fontSize: 16, color: "#F97316" }}>{l.views || 0}</span></td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button onClick={() => { window.open(`https://www.renthound.com/?listing=${l.id}`, "_blank"); }} style={{ background: "#eff6ff", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#1d4ed8" }}>View</button>
                      <button onClick={() => setEditingListing(l)} style={{ background: "#f3f4f6", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#374151" }}>Edit</button>
                      {isBilling(l) && <button onClick={() => handleCancelSubscription(l.id)} disabled={cancellingId === l.id} style={{ background: "#fff7ed", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#c2410c", whiteSpace: "nowrap" }}>{cancellingId === l.id ? "..." : "Cancel subscription"}</button>}
                      <button onClick={() => handleDelete(l)} disabled={deletingId === l.id} style={{ background: "#fef2f2", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", color: "#991b1b" }}>{deletingId === l.id ? "..." : "Delete"}</button>
                    </div>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
      <footer style={{ background: "#0f1f3d", color: "rgba(255,255,255,0.5)", fontSize: 12, textAlign: "center", padding: "1.5rem" }}>
        <p style={{ margin: 0 }}>© 2026 RentHound · renthound.com</p>
      </footer>
    </div>
  );
}

const MobileNavItem = ({ icon, label, onClick }) => (
  <button onClick={onClick}
    style={{ background: "transparent", border: "none", borderBottom: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.85)", fontSize: 15, fontWeight: 500, padding: "14px 0", textAlign: "left", cursor: "pointer", width: "100%", display: "flex", alignItems: "center", gap: 12 }}>
    {icon && <Icon name={icon} size={18} color="rgba(255,255,255,0.55)" />}
    {label}
  </button>
);

const Nav = ({ onBack, backLabel, user, onSignIn, onSignOut, setPage, onPostListing }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [saved] = useSavedListings();
  const savedCount = Object.keys(saved).length;
  const close = () => setMenuOpen(false);
  const openSaved = () => { window.history.pushState({}, "", "/saved"); setPage("saved"); close(); };
  return (
    <div style={{ position: "relative", zIndex: 200 }}>
      <style>{`
        .rh-desktop { display: flex; }
        .rh-burger { display: none; }
        .rh-mobile-menu { display: none; }
        @media (max-width: 768px) {
          .rh-desktop { display: none !important; }
          .rh-burger { display: flex !important; }
          .rh-mobile-menu { display: flex !important; }
        }
      `}</style>
      <nav style={{ background: "#0f1f3d", padding: "0 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", height: 56 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} onClick={() => { close(); setPage("home"); }}>
          <LogoMark />
          <span style={{ color: "white", fontWeight: 700, fontSize: 18 }}><span>Rent</span><span style={{ color: "#F97316" }}>Hound</span></span>
        </div>
        <div className="rh-desktop" style={{ gap: 12, alignItems: "center" }}>
          {onBack && <button onClick={onBack} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>{backLabel || "← Back"}</button>}
          <span onClick={() => { window.history.pushState({}, "", "/blog"); setPage("blog"); }} style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, cursor: "pointer" }}>Blog</span>
          <span onClick={() => setPage("pricing")} style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, cursor: "pointer" }}>Pricing</span>
          <span onClick={openSaved} style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Icon name="heart" size={14} color={savedCount ? "#F97316" : "currentColor"} fill={savedCount ? "#F97316" : "none"} />Saved{savedCount > 0 && ` (${savedCount})`}
          </span>
          {user ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span onClick={() => setPage("dashboard")} style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, cursor: "pointer", textDecoration: "underline" }}>Dashboard</span>
              <button onClick={onSignOut} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>Sign out</button>
            </div>
          ) : (
            <button onClick={onSignIn} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.3)", color: "white", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer" }}>Sign in</button>
          )}
          <button onClick={onPostListing} style={{ background: "#F97316", border: "none", color: "white", borderRadius: 6, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Post a listing</button>
        </div>
        <button className="rh-burger" onClick={() => setMenuOpen(o => !o)}
          style={{ background: "transparent", border: "none", color: "white", fontSize: 26, cursor: "pointer", padding: "4px 8px", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
          <Icon name={menuOpen ? "x" : "menu"} size={26} />
        </button>
      </nav>
      {menuOpen && (
        <div className="rh-mobile-menu" style={{ background: "#0f1f3d", borderTop: "1px solid rgba(255,255,255,0.1)", padding: "0.5rem 1.5rem 1.5rem", flexDirection: "column", position: "absolute", top: 56, left: 0, right: 0, zIndex: 199, boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
          <MobileNavItem icon="home" label="Home" onClick={() => { setPage("home"); close(); }} />
          <MobileNavItem icon="file" label="Blog" onClick={() => { window.history.pushState({}, "", "/blog"); setPage("blog"); close(); }} />
          <MobileNavItem icon="wallet" label="Pricing" onClick={() => { setPage("pricing"); close(); }} />
          <MobileNavItem icon="heart" label={savedCount > 0 ? `Saved (${savedCount})` : "Saved"} onClick={openSaved} />
          <MobileNavItem icon="file" label="Terms" onClick={() => { window.history.pushState({}, "", "/terms"); setPage("terms"); close(); }} />
          <MobileNavItem icon="lock" label="Privacy" onClick={() => { setPage("privacy"); close(); }} />
          {user ? (
            <>
              <MobileNavItem icon="chart" label="Dashboard" onClick={() => { setPage("dashboard"); close(); }} />
              <MobileNavItem label="Sign out" onClick={() => { onSignOut(); close(); }} />
            </>
          ) : (
            <MobileNavItem label="Sign in" onClick={() => { onSignIn(); close(); }} />
          )}
          <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <button onClick={() => { onPostListing(); close(); }}
              style={{ width: "100%", background: "#F97316", border: "none", color: "white", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
              Post a listing
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
const Footer = ({ setPage }) => (
  <footer style={{ background: "#0f1f3d", color: "rgba(255,255,255,0.5)", fontSize: 12, textAlign: "center", padding: "1.5rem" }}>
    <p style={{ margin: "0 0 4px" }}>© 2026 RentHound · renthound.com · Built for renters, by renters.</p>
    <p style={{ margin: "0 0 8px" }}>
      <span onClick={() => setPage("about")} style={{ cursor: "pointer", textDecoration: "underline", marginRight: 12 }}>About</span>
      <span onClick={() => setPage("pricing")} style={{ cursor: "pointer", textDecoration: "underline", marginRight: 12 }}>Pricing</span>
      <span onClick={() => { window.history.pushState({}, "", "/blog"); setPage("blog"); }} style={{ cursor: "pointer", textDecoration: "underline", marginRight: 12 }}>Blog</span>
      <span onClick={() => { window.history.pushState({}, "", "/terms"); setPage("terms"); }} style={{ cursor: "pointer", textDecoration: "underline", marginRight: 12 }}>Terms</span>
      <span onClick={() => setPage("privacy")} style={{ cursor: "pointer", textDecoration: "underline", marginRight: 12 }}>Privacy</span>
      <a href="mailto:support@renthound.com" style={{ color: "rgba(255,255,255,0.5)", marginRight: 12 }}>Contact</a>
      <a href="mailto:support@renthound.com" style={{ color: "rgba(255,255,255,0.5)" }}>Advertise</a>
    </p>
    <p style={{ margin: 0, fontSize: 11, display: "flex", flexWrap: "wrap", justifyContent: "center", columnGap: 6, rowGap: 2 }}>
      <span>Rentals in:</span>
      {Object.values(CITIES).map((c, i) => (
        <span key={c.slug} style={{ whiteSpace: "nowrap" }}>
          <a href={`/rentals/${c.slug}`} style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>{c.name.split(",")[0]}</a>
          {i < Object.values(CITIES).length - 1 && <span style={{ marginLeft: 6 }}>·</span>}
        </span>
      ))}
    </p>
  </footer>
);

function AboutPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  useSeo({ title: "About RentHound", description: "RentHound puts rentals from Redfin, Zillow, Realtor.com, Apartments.com and local landlords in one search, so you can compare them side by side.", path: "/about" });
  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav onBack={() => setPage("home")} user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ background: "linear-gradient(135deg, #0f1f3d 0%, #1a3566 100%)", padding: "4rem 2rem", textAlign: "center" }}>
        <h1 style={{ color: "white", fontSize: 36, fontWeight: 700, margin: "0 0 1rem" }}>About RentHound</h1>
        <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 18, maxWidth: 600, margin: "0 auto" }}>Find anything for rent — apartments, houses, and short-term stays — all in one place.</p>
      </div>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "3rem 1.5rem 5rem" }}>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem", marginBottom: "1.5rem", lineHeight: 1.8, color: "#374151", fontSize: 15 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111", marginTop: 0 }}>Our Mission</h2>
          <p>Searching for a rental shouldn't mean jumping between a dozen different websites. RentHound was built to solve that problem — bringing together listings from across the web into one clean, easy-to-use platform.</p>
          <p>Whether you're looking for a long-term apartment, a house to rent, or a short-term stay, RentHound searches multiple platforms at once and shows you everything in one place.</p>
        </div>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem", marginBottom: "1.5rem" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111", marginTop: 0 }}>Where Our Listings Come From</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem", marginTop: "1.5rem" }}>
            {[{ name: "Redfin", color: "#d73f3f", desc: "Long-term rental listings across the US" }, { name: "Zillow", color: "#006AFF", desc: "Comprehensive rental and real estate data" }, { name: "Realtor.com", color: "#D92228", desc: "Rentals listed by agents and property managers" }, { name: "Apartments.com", color: "#5E8E3E", desc: "Apartment communities and multi-unit buildings" }].map(s => (
              <div key={s.name} style={{ background: "#f8f7f4", borderRadius: 8, padding: "1.25rem", borderLeft: `4px solid ${s.color}` }}>
                <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 16, color: "#111" }}>{s.name}</p>
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#111", marginTop: 0 }}>Get in Touch</h2>
          <a href="mailto:support@renthound.com" style={{ display: "inline-block", background: "#F97316", color: "white", padding: "12px 24px", borderRadius: 8, fontWeight: 700, fontSize: 15, textDecoration: "none" }}>Contact Us →</a>
        </div>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

function PrivacyPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  useSeo({ title: "Privacy Policy — RentHound", description: "What RentHound collects, the cookies Google Analytics and Google AdSense use, and how to opt out.", path: "/privacy" });
  const h2 = { fontSize: 18, fontWeight: 700, color: "#111", margin: "1.75rem 0 0.5rem" };
  const link = { color: "#F97316" };
  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav onBack={() => setPage("home")} user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "3rem 1.5rem 5rem" }}>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: "#111", marginBottom: "0.5rem" }}>Privacy Policy</h1>
        <p style={{ color: "#6b7280", fontSize: 14, marginBottom: "2rem" }}>Last updated: November 1, 2026</p>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem", lineHeight: 1.8, color: "#374151", fontSize: 15 }}>
          <p style={{ marginTop: 0 }}>This page explains what information RentHound (renthound.com) collects, why, and the choices you have. You can search rentals without creating an account.</p>

          <h2 style={h2}>Information we collect</h2>
          <ul>
            <li><strong>Email address</strong>: if you sign in, so we can send you a sign-in link.</li>
            <li><strong>Searches</strong>: the location and filters you enter. We send these to our listing data provider to get results.</li>
            <li><strong>Listings you post</strong>: the details and photos you add, and your email address so we can forward renters' messages to you.</li>
            <li><strong>Messages to landlords</strong>: the name, email and message you enter on a listing's contact form, which we forward to that landlord by email.</li>
            <li><strong>Reports</strong>: when you report a listing, which listing it was.</li>
            <li><strong>Payments</strong>: handled by Stripe. We see whether a payment went through, but never your card number.</li>
            <li><strong>Usage data</strong>: pages visited, how you got here, and your browser and device type, collected by Google Analytics (see below).</li>
          </ul>

          <h2 style={h2}>Cookies and similar technologies</h2>
          <p>Cookies are small files a website stores in your browser. RentHound's own code doesn't set any, but these services we use do:</p>
          <ul>
            <li><strong>Google Analytics</strong> uses cookies to count visitors and see which pages are used, so we can improve the site. Google processes this data under its own <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={link}>privacy policy</a>.</li>
            <li><strong>Google AdSense.</strong> RentHound may show ads served by Google. Third-party vendors, including Google, use cookies to serve ads based on your earlier visits to this and other websites. Google's advertising cookies let it and its partners show you ads based on those visits.</li>
          </ul>
          <p>We also use your browser's own storage (not cookies) to remember the listings you save with the heart button and to keep you signed in. This stays on your device; clearing your browser data removes it.</p>

          <h2 style={h2}>Your choices</h2>
          <ul>
            <li>Turn off personalized ads from Google in <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" style={link}>Google's Ad Settings</a>.</li>
            <li>Opt out of other vendors' personalized ads at <a href="https://www.aboutads.info/choices" target="_blank" rel="noopener noreferrer" style={link}>aboutads.info</a>.</li>
            <li>Stop Google Analytics from collecting your visits with the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" style={link}>Google Analytics opt-out add-on</a>.</li>
            <li>Block or delete cookies in your browser's settings. The site still works without them.</li>
          </ul>

          <h2 style={h2}>Who we share information with</h2>
          <p>We don't sell your email address or other contact details. We share information only with the services that run RentHound: Supabase (accounts and data storage), Stripe (payments), Resend (forwarding messages to landlords), our listing data provider (searches), and Google (analytics and ads, as described above). Listings come from Redfin, Zillow, Realtor.com, Apartments.com and landlords who post here; when you open a listing on one of those sites, their privacy policy applies.</p>

          <h2 style={h2}>Keeping and deleting your information</h2>
          <p>We keep your account and listings until you delete them or ask us to. You can delete your listings from your dashboard, or email us to delete your account and everything connected to it.</p>

          <h2 style={h2}>Children</h2>
          <p>RentHound is not meant for children under 13, and we don't knowingly collect their information.</p>

          <h2 style={h2}>Changes</h2>
          <p>If we change this policy, we'll update the date at the top of this page.</p>

          <h2 style={h2}>Contact us</h2>
          <p style={{ marginBottom: 0 }}>Questions, or want your information deleted? Email <a href="mailto:support@renthound.com" style={link}>support@renthound.com</a>.</p>
        </div>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

// Saved hearts live in this browser, so they're still there after a refresh or on the next visit.
// Every page that shows hearts uses this hook; the event keeps them in step with each other.
const SAVED_KEY = "rh-saved-listings";
const readSaved = () => { try { return JSON.parse(localStorage.getItem(SAVED_KEY)) || {}; } catch { return {}; } };
// Opening a listing adds a browser history entry, so the browser's Back button closes it and
// returns to the results (at the same scroll spot) instead of leaving the page.
// `scope` keeps the home search, city pages and Saved from reacting to each other's entries.
function useListingHistory(scope) {
  const [selectedListing, setSelectedListing] = useState(null);
  const returnScroll = useRef(0);
  useEffect(() => {
    const onPop = (e) => setSelectedListing(e.state?.rhScope === scope ? e.state.rhListing : null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [scope]);
  useEffect(() => {
    if (!selectedListing && returnScroll.current) {
      window.scrollTo(0, returnScroll.current);
      returnScroll.current = 0;
    }
  }, [selectedListing]);
  const openListing = useCallback((l) => {
    if (!window.history.state?.rhListing) returnScroll.current = window.scrollY;
    window.history.pushState({ rhScope: scope, rhListing: l }, "", window.location.href);
    setSelectedListing(l);
  }, [scope]);
  const closeListing = useCallback(() => {
    if (window.history.state?.rhListing) window.history.back();
    else setSelectedListing(null);
  }, []);
  return [selectedListing, openListing, closeListing, setSelectedListing];
}

function useSavedListings() {
  const [saved, setSaved] = useState(readSaved);
  useEffect(() => {
    const sync = () => setSaved(readSaved());
    window.addEventListener("rh-saved-change", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("rh-saved-change", sync); window.removeEventListener("storage", sync); };
  }, []);
  // Keeps a copy of each saved listing so the Saved page can show it without searching again.
  // Hearts from before the Saved page existed are stored as `true` until SavedPage looks them up.
  const writeSaved = useCallback((next) => {
    try { localStorage.setItem(SAVED_KEY, JSON.stringify(next)); } catch {}
    setSaved(next);
    window.dispatchEvent(new Event("rh-saved-change"));
  }, []);
  const toggleSave = useCallback((l) => {
    const next = { ...readSaved() };
    if (next[l.id]) delete next[l.id]; else next[l.id] = { ...l, savedAt: Date.now() };
    writeSaved(next);
  }, [writeSaved]);
  const replaceSaved = useCallback((update) => writeSaved(update(readSaved())), [writeSaved]);
  return [saved, toggleSave, replaceSaved];
}

// Some sites send a generic "image coming soon" picture instead of a real photo.
const isStandInPhoto = (url) => /no[-_]?image|image[-_]?coming|coming[-_]?soon|placeholder|nophoto/i.test(url || "");

// Shown when a listing has no photo, or its photo is a site's "image coming soon" stand-in.
const NoPhoto = ({ height = 190 }) => (
  <div style={{ width: "100%", height, background: "linear-gradient(135deg, #f4efe6 0%, #ebe4d6 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#b8a888" }}>
    <Icon name="image" size={30} strokeWidth={1.5} />
    <span style={{ fontSize: 12, fontWeight: 500 }}>Photos on the full listing</span>
  </div>
);

function ListingCard({ l, saved, onSave, onClick }) {
  const [imgFailed, setImgFailed] = useState(false);
  const hasPhoto = l.img && !imgFailed && !isStandInPhoto(l.img);
  const specs = [
    l.beds != null && { icon: "bed", text: l.beds === 0 ? "Studio" : `${l.beds} bd` },
    l.baths != null && { icon: "bath", text: `${l.baths} ba` },
    l.sqft && { icon: "ruler", text: `${Number(l.sqft).toLocaleString()} sqft` },
  ].filter(Boolean);
  return (
    <div className="rh-card" onClick={() => onClick(l)} style={{ background: "white", borderRadius: 14, border: "1px solid #ebe8e2", overflow: "hidden", cursor: "pointer", boxShadow: "0 1px 2px rgba(15,31,61,0.04)" }}>
      <div style={{ position: "relative", overflow: "hidden" }}>
        {hasPhoto ? <img src={l.img} alt={l.title} loading="lazy" onError={() => setImgFailed(true)} style={{ width: "100%", height: 190, objectFit: "cover", display: "block" }} /> : <NoPhoto />}
        <button className="rh-heart" onClick={e => { e.stopPropagation(); onSave(l); }} aria-label={saved ? "Remove from saved" : "Save listing"} aria-pressed={!!saved}
          style={{ position: "absolute", top: 10, right: 10, background: "rgba(255,255,255,0.95)", border: "none", borderRadius: "50%", width: 34, height: 34, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
          <Icon name="heart" size={17} color={saved ? "#F97316" : "#374151"} fill={saved ? "#F97316" : "none"} />
        </button>
        <span style={{ position: "absolute", top: 10, left: 10, display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(255,255,255,0.95)", color: "#1f2937", fontSize: 11, fontWeight: 600, padding: "4px 9px", borderRadius: 999, boxShadow: "0 2px 8px rgba(0,0,0,0.12)" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: sourceColor[l.source] || "#6b7280" }} />{sourceLabel[l.source] || l.source}
        </span>
      </div>
      <div style={{ padding: "0.9rem 1rem 1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <p style={{ margin: 0, fontWeight: 800, fontSize: 19, color: "#0f1f3d", letterSpacing: "-0.3px" }}>
            {l.price ? <>{l.priceFrom && <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 500, letterSpacing: 0 }}>From </span>}${Number(l.price).toLocaleString()}<span style={{ color: "#6b7280", fontSize: 13, fontWeight: 500, letterSpacing: 0 }}>/mo</span></> : <span style={{ color: "#6b7280", fontSize: 14, fontWeight: 600, letterSpacing: 0 }}>See price on listing</span>}
          </p>
          <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 500, textTransform: "capitalize" }}>{l.type}</span>
        </div>
        <p style={{ margin: "0 0 3px", fontWeight: 600, fontSize: 14, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.title}</p>
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}><Icon name="pin" size={13} />{l.location}</p>
        {specs.length > 0 && (
          <div style={{ display: "flex", gap: 14, fontSize: 13, color: "#374151", borderTop: "1px solid #f1efea", paddingTop: 10, marginTop: 12 }}>
            {specs.map(s => <span key={s.icon} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Icon name={s.icon} size={15} color="#9ca3af" />{s.text}</span>)}
          </div>
        )}
      </div>
    </div>
  );
}

// The RentHound logo: the foxhound from the homepage drawing inside a map pin.
// public/favicon.svg is the same drawing; change both together.
function LogoMark({ height = 32 }) {
  return (
    <svg width={height * 26 / 33} height={height} viewBox="0 -1 26 33" role="img" aria-label="RentHound" style={{ flexShrink: 0, display: "block" }}>
      <defs><clipPath id="rh-logo-clip"><circle cx="13" cy="12" r="10" /></clipPath></defs>
      <path d="M13,31 C4,21 0,16 0,12 A13,13 0 0 1 26,12 C26,16 22,21 13,31 Z" fill="#F97316" />
      <circle cx="13" cy="12" r="10" fill="#fde7cf" />
      <g clipPath="url(#rh-logo-clip)">
        <svg x="3.5" y="2.5" width="19" height="19" viewBox="0 0 64 64">
          <path d="M18,60 C15,44 16,28 24,21 C28,16.5 32,14.5 36,15 C39,15.5 40.5,18 42,20.5 L55.5,23.5 C58.5,24.2 60,27.5 59,30.5 C58.5,32.5 56,33.5 53,34 L48,35 C47.5,37.5 45,39.5 41.5,40 C40,46 40.5,53 42,60 Z" fill="#d9822b" />
          <path d="M42,20.5 L55.5,23.5 C58.5,24.2 60,27.5 59,30.5 C58.5,32.5 56,33.5 53,34 L48,35 C47.5,37.5 45,39.5 41.5,40 C43,33 43,26 42,20.5 Z" fill="#fff" />
          <path d="M42,20.8 C40.5,17.8 38.5,15.6 35.8,15.1 C37.8,17.2 39.3,19.8 40.2,23 Z" fill="#fff" />
          <path d="M27,20 C17,20 13,33 16,45 C17.5,50 23,50 25.5,44 C28,37 30,26 27,20 Z" fill="#8a4b1c" />
          <circle cx="36.8" cy="22.3" r="2.2" fill="#0f1f3d" />
          <ellipse cx="58" cy="26.6" rx="2.8" ry="2.6" fill="#1f2937" />
        </svg>
      </g>
    </svg>
  );
}

function HoundIllustration() {
  return (
    <svg viewBox="0 0 320 200" width="260" style={{ maxWidth: "100%", height: "auto", display: "block", margin: "0 auto" }} role="img" aria-label="A foxhound sniffing a trail across a map to a location pin">
      <ellipse cx="165" cy="189" rx="150" ry="7" fill="#e9e4da"/>
      <g stroke="#d9c7a7" strokeWidth="1.5" strokeLinejoin="round">
        <polygon points="105,186 120,130 180,127 172,183" fill="#fffaf2"/>
        <polygon points="172,183 180,127 240,125 240,181" fill="#f3e8d6"/>
        <polygon points="240,181 240,125 300,122 310,178" fill="#fffaf2"/>
      </g>
      <path d="M186,140 q14,-6 26,2 q10,8 -2,16 q-14,6 -22,-4 z" fill="#cfe8c9"/>
      <path d="M244,170 q18,-14 28,-6 t36,-10" fill="none" stroke="#bfdbfe" strokeWidth="5" strokeLinecap="round"/>
      <path d="M125,150 L176,147 M118,170 L238,166 M210,128 L206,182" stroke="#ebdcc3" strokeWidth="2"/>
      <path d="M157,181 C174,172 180,152 205,160 S255,152 272,146" fill="none" stroke="#0f1f3d" strokeWidth="2.5" strokeDasharray="5 5" strokeLinecap="round"/>
      <path d="M274,146 c-10,-12 -14,-18 -14,-25 a14,14 0 0 1 28,0 c0,7 -4,13 -14,25 z" fill="#F97316"/>
      <circle cx="274" cy="121" r="5" fill="white"/>
      <path d="M26,122 Q8,112 12,86" fill="none" stroke="#d9822b" strokeWidth="7" strokeLinecap="round"/>
      <path d="M12,94 Q11,88 12,84" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round"/>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="9">
        <path d="M50,132 L44,156 L50,180" stroke="#c4711f"/>
        <path d="M50,172 L50,180" stroke="#f1ece4"/>
        <path d="M84,136 L80,160 L76,181" stroke="#c4711f"/>
        <path d="M78,172 L76,181" stroke="#f1ece4"/>
      </g>
      <ellipse cx="64" cy="128" rx="44" ry="20" fill="#d9822b"/>
      <ellipse cx="36" cy="134" rx="15" ry="13" fill="#d9822b"/>
      <g fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="10">
        <path d="M38,140 L28,160 L34,181" stroke="#d9822b"/>
        <path d="M31,171 L34,181" stroke="white"/>
        <path d="M98,138 L104,160 L112,180" stroke="#d9822b"/>
        <path d="M108,171 L112,180" stroke="white"/>
      </g>
      <g fill="white"><ellipse cx="37" cy="182.5" rx="7" ry="3.5"/><ellipse cx="116" cy="181.5" rx="7" ry="3.5"/><ellipse cx="53" cy="181.5" rx="6" ry="3"/><ellipse cx="79" cy="182" rx="6" ry="3"/></g>
      <path d="M72,145 Q92,146 104,132 Q100,144 88,149 Z" fill="white"/>
      <path d="M30,118 Q60,102 92,114 Q82,127 60,128 Q38,129 30,118 Z" fill="#0f1f3d"/>
      <path d="M90,110 Q114,116 132,150 L114,166 Q104,142 96,140 Z" fill="#d9822b"/>
      <g transform="translate(124 160) rotate(35)">
        <ellipse cx="0" cy="0" rx="18" ry="14" fill="#d9822b"/>
        <path d="M4,-9 C14,-10 28,-8 34,-5 C38,-3 38,4 34,6 C26,9 14,10 4,8 Z" fill="#d9822b"/>
        <path d="M2,-9 C14,-10 26,-8 33,-5.5 L32,-3 C24,-5 12,-6 2,-5 Z" fill="white"/>
        <path d="M10,6 C18,8 28,7 33,5" fill="none" stroke="#b9661c" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M33,-6 C38,-6 40,-3 40,0 C40,3 37,4 34,3 C32,1 32,-4 33,-6 Z" fill="#1f2937"/>
        <circle cx="37" cy="-2.6" r="1" fill="white" opacity="0.7"/>
        <path d="M3,-7 q4,3 8,0" fill="none" stroke="#0f1f3d" strokeWidth="2" strokeLinecap="round"/>
        <path d="M-4,-11 Q-16,-6 -15,10 Q-14,22 -6,24 Q0,20 0,8 Q1,-4 -4,-11 Z" fill="#8a4b1c"/>
      </g>
      <g fill="none" stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round">
        <path d="M162,180 q4,3 0,6"/><path d="M168,177 q5,5 0,10"/>
      </g>
    </svg>
  );
}

function PhotoLightbox({ photos, index, setIndex, onClose }) {
  const touchX = useRef(null);
  const prev = () => setIndex(i => (i - 1 + photos.length) % photos.length);
  const next = () => setIndex(i => (i + 1) % photos.length);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); else if (e.key === "ArrowLeft") prev(); else if (e.key === "ArrowRight") next(); };
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = overflow; };
  });
  const arrow = { position: "absolute", top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.15)", color: "white", border: "none", borderRadius: "50%", width: 48, height: 48, fontSize: 26, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
  return (
    <div onClick={onClose} onTouchStart={e => { touchX.current = e.touches[0].clientX; }} onTouchEnd={e => { if (touchX.current === null) return; const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null; if (dx > 50) prev(); else if (dx < -50) next(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ position: "absolute", top: 18, left: 20, color: "white", fontSize: 14 }}>{index + 1} / {photos.length}</span>
      <button onClick={onClose} aria-label="Close" style={{ position: "absolute", top: 10, right: 14, background: "none", border: "none", color: "white", fontSize: 32, cursor: "pointer", lineHeight: 1 }}>×</button>
      <img src={photos[index]} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: "92vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 4 }} />
      {photos.length > 1 && <>
        <button onClick={e => { e.stopPropagation(); prev(); }} aria-label="Previous photo" style={{ ...arrow, left: 16 }}>‹</button>
        <button onClick={e => { e.stopPropagation(); next(); }} aria-label="Next photo" style={{ ...arrow, right: 16 }}>›</button>
      </>}
    </div>
  );
}

function ListingDetail({ listing, allListings, onBack, onSelectListing, setPage, user, onSignIn, onSignOut, onPostListing }) {
  const [saved, toggleSave] = useSavedListings();
  const [reported, setReported] = useState(false); const [copied, setCopied] = useState(false); const [showContact, setShowContact] = useState(false);
  const nearby = allListings.filter(l => l.id !== listing.id).slice(0, 4);
  const [photoFailed, setPhotoFailed] = useState(false);
  const allPhotos = photoFailed ? [] : listing.photos && listing.photos.length > 0 ? listing.photos : (listing.img ? [listing.img] : []);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const openPhoto = (i) => setLightboxIndex(i);
  useEffect(() => { fetch(`${API_URL}/api/listings/${listing.id}/view`, { method: "POST" }).catch(() => {}); }, [listing.id]);
  useEffect(() => { window.scrollTo(0, 0); }, [listing.id]);
  // Apartments.com search results have no baths or sqft; fetch the listing's floor plans for them.
  const [floorPlans, setFloorPlans] = useState(null);
  useEffect(() => {
    if (listing.source !== "apartments" || !String(listing.id).startsWith("apartments_")) return;
    const key = String(listing.id).slice("apartments_".length);
    fetch(`${API_URL}/api/listings/apartments/${encodeURIComponent(key)}/floorplans`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && Array.isArray(data.floorPlans) && data.floorPlans.length) setFloorPlans(data); })
      .catch(() => {});
  }, [listing.id, listing.source]);
  const span = (min, max, fmt = String) => min == null ? null : (max != null && max !== min ? `${fmt(min)}–${fmt(max)}` : fmt(min));
  const planBeds = floorPlans ? floorPlans.floorPlans.map(f => f.beds).filter(n => n !== null) : [];
  const bedsValue = planBeds.length ? span(Math.min(...planBeds), Math.max(...planBeds), n => n === 0 ? "Studio" : n)
    : listing.beds !== null ? (listing.beds === 0 ? "Studio" : listing.beds) : "—";
  const bathsValue = listing.baths !== null ? listing.baths : (floorPlans && span(floorPlans.baths, floorPlans.bathsMax)) || "—";
  const sqftValue = listing.sqft ? listing.sqft.toLocaleString() : (floorPlans && span(floorPlans.sqft, floorPlans.sqftMax, n => n.toLocaleString())) || "—";

  const handleShare = () => {
    const shareUrl = `https://renthound.com/?listing=${encodeURIComponent(listing.id)}`;
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleReport = async () => {
    try { await fetch(`${API_URL}/api/listings/${listing.id}/report`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title: listing.title }) }); } catch {}
    setReported(true); setTimeout(() => setReported(false), 3000);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      {showContact && <ContactModal listing={listing} onClose={() => setShowContact(false)} />}
      {lightboxIndex !== null && <PhotoLightbox photos={allPhotos} index={lightboxIndex} setIndex={setLightboxIndex} onClose={() => setLightboxIndex(null)} />}
      <Nav onBack={onBack} backLabel="← Back to results" user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>
        <div style={{ position: "relative", marginBottom: "1.5rem", borderRadius: 12, overflow: "hidden" }}>
          {allPhotos.length === 0 ? <NoPhoto height={440} />
          : allPhotos.length === 1 ? <img src={allPhotos[0]} alt={listing.title} onClick={() => openPhoto(0)} onError={() => setPhotoFailed(true)} style={{ width: "100%", height: 440, objectFit: "cover", display: "block", cursor: "zoom-in" }} />
          : allPhotos.length === 2 ? <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>{allPhotos.map((p, i) => <img key={i} src={p} alt="" onClick={() => openPhoto(i)} style={{ width: "100%", height: 440, objectFit: "cover", display: "block", cursor: "zoom-in" }} />)}</div>
          : <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 4 }}>
              <img src={allPhotos[0]} alt={listing.title} onClick={() => openPhoto(0)} style={{ width: "100%", height: 440, objectFit: "cover", display: "block", cursor: "zoom-in" }} />
              <div style={{ display: "grid", gridTemplateRows: `repeat(${Math.min(allPhotos.length - 1, 4)}, 1fr)`, gap: 4 }}>
                {allPhotos.slice(1, 5).map((p, i) => (
                  <div key={i} onClick={() => openPhoto(i + 1)} style={{ position: "relative", cursor: "zoom-in" }}>
                    <img src={p} alt="" style={{ width: "100%", height: Math.floor(440 / Math.min(allPhotos.length - 1, 4)), objectFit: "cover", display: "block" }} />
                    {i === 3 && allPhotos.length > 5 && <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", color: "white", fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>+{allPhotos.length - 5} more</div>}
                  </div>
                ))}
              </div>
            </div>}
          {allPhotos.length > 1 && <button onClick={() => openPhoto(0)} style={{ position: "absolute", bottom: 12, right: 12, background: "white", color: "#111", border: "1px solid #e5e7eb", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>View all {allPhotos.length} photos</button>}
        </div>
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "1.5rem 2rem", marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem", flexWrap: "wrap", gap: 12 }}>
            <div><h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 700, color: "#111" }}>{listing.title}</h1><p style={{ margin: 0, fontSize: 15, color: "#6b7280" }}><Icon name="pin" size={15} style={{ marginRight: 4, marginTop: -2 }} />{listing.location}</p></div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
              {listing.price ? <span style={{ fontSize: 24, fontWeight: 700, color: "#111" }}>{listing.priceFrom && <span style={{ fontSize: 15, fontWeight: 400, color: "#6b7280" }}>From </span>}${Number(listing.price).toLocaleString()}<span style={{ fontSize: 14, fontWeight: 400, color: "#6b7280" }}>/mo</span></span> : <span style={{ fontSize: 15, color: "#6b7280" }}>Price on listing</span>}
              <span style={{ background: sourceColor[listing.source] || "#6b7280", color: "white", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 4 }}>{sourceLabel[listing.source] || listing.source}</span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 24, padding: "1rem 0", borderTop: "1px solid #f3f4f6", borderBottom: "1px solid #f3f4f6", marginBottom: "1.5rem" }}>
            {[{ label: "Bedrooms", value: bedsValue }, { label: "Bathrooms", value: bathsValue }, { label: "Sq ft", value: sqftValue }, { label: "Type", value: listing.type }].map(s => (
              <div key={s.label} style={{ textAlign: "center" }}><p style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "#111", textTransform: "capitalize" }}>{s.value}</p><p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>{s.label}</p></div>
            ))}
          </div>
          {floorPlans && floorPlans.floorPlans.length > 1 && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>Floor plans</h3>
              {floorPlans.floorPlans.map((f, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "8px 0", borderBottom: "1px solid #f3f4f6", fontSize: 14, color: "#374151" }}>
                  <span>{[f.beds === 0 ? "Studio" : f.beds != null && `${f.beds} bd`, f.baths != null && `${f.baths} ba`, f.sqft && `${f.sqft.toLocaleString()} sq ft`].filter(Boolean).join(" · ")}</span>
                  <span style={{ color: "#6b7280" }}>{f.rent}</span>
                </div>
              ))}
            </div>
          )}
          {listing.description && (
            <div style={{ marginBottom: "1.5rem" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111", margin: "0 0 8px" }}>About this listing</h3>
              <p style={{ fontSize: 15, color: "#374151", lineHeight: 1.8, margin: 0, whiteSpace: "pre-line" }}>{listing.description}</p>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {listing.url && <a href={listing.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 180, background: "#F97316", color: "white", textAlign: "center", padding: "12px", borderRadius: 8, fontWeight: 700, fontSize: 15, textDecoration: "none", display: "block" }}>View Full Listing →</a>}
            {listing.source === "user" && <button onClick={() => setShowContact(true)} style={{ flex: 1, minWidth: 180, background: "#0f1f3d", color: "white", border: "none", borderRadius: 8, padding: "12px", fontSize: 15, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Icon name="mail" size={17} /> Contact Landlord</button>}
            <button onClick={handleShare} style={{ background: copied ? "#d1fae5" : "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 20px", fontSize: 14, cursor: "pointer", fontWeight: 500, color: copied ? "#065f46" : "#374151" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name={copied ? "check" : "share"} size={15} />{copied ? "Link copied!" : "Share listing"}</span></button>
            <button onClick={handleReport} style={{ background: reported ? "#fef2f2" : "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 20px", fontSize: 14, cursor: "pointer", fontWeight: 500, color: reported ? "#991b1b" : "#6b7280" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name={reported ? "check" : "flag"} size={15} />{reported ? "Reported" : "Report listing"}</span></button>
          </div>
        </div>
        {nearby.length > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 1rem", color: "#111" }}>You might also like</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
              {nearby.map(l => <ListingCard key={l.id} l={l} saved={saved[l.id]} onSave={toggleSave} onClick={onSelectListing} />)}
            </div>
          </div>
        )}
        <button onClick={onBack} style={{ background: "transparent", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: "pointer", color: "#374151" }}>← Back to search results</button>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

// City pages load a whole city at once, so their filters narrow those results in the browser
// instead of running a new search. Listings missing beds or baths aren't hidden by those filters.
const CITY_FILTERS_DEFAULT = { type: "all", maxPrice: "", beds: "any", baths: "any", source: "all", sort: "recommended" };

function filterCityListings(listings, f) {
  const filtered = listings.filter(l => {
    if (f.source !== "all" && l.source !== f.source) return false;
    if (f.type !== "all" && l.type && l.type !== f.type) return false;
    if (f.maxPrice && !(l.price && l.price <= Number(f.maxPrice))) return false;
    if (f.beds !== "any" && l.beds != null && (f.beds === "3+" ? Number(l.beds) < 3 : Number(l.beds) !== Number(f.beds))) return false;
    if (f.baths !== "any" && l.baths != null && Number(l.baths) < (f.baths === "3+" ? 3 : Number(f.baths))) return false;
    return true;
  });
  if (f.sort === "recommended") return filtered;
  const dir = f.sort === "price_asc" ? 1 : -1;
  return [...filtered].sort((a, b) => {
    if (!a.price && !b.price) return 0;
    if (!a.price) return 1;
    if (!b.price) return -1;
    return (a.price - b.price) * dir;
  });
}

function CityFilterBar({ filters, onChange, sourceCounts }) {
  const set = (key) => (e) => onChange({ ...filters, [key]: e.target.value });
  const field = { border: "1px solid #e5e7eb", borderRadius: 6, padding: "7px 10px", fontSize: 13, background: "white", color: "#111" };
  const label = { fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" };
  const changed = Object.keys(CITY_FILTERS_DEFAULT).some(k => filters[k] !== CITY_FILTERS_DEFAULT[k]);
  return (
    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 120px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Type</label>
          <select value={filters.type} onChange={set("type")} style={field}>
            <option value="all">All types</option><option value="apartment">Apartment</option><option value="house">House</option>
          </select>
        </div>
        <div style={{ flex: "1 1 120px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Max price/mo</label>
          <input value={filters.maxPrice} onChange={set("maxPrice")} placeholder="$3,000" type="number" min="0" style={{ ...field, outline: "none" }} />
        </div>
        <div style={{ flex: "1 1 90px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Beds</label>
          <select value={filters.beds} onChange={set("beds")} style={field}>
            <option value="any">Any</option><option value="0">Studio</option><option value="1">1 bd</option><option value="2">2 bd</option><option value="3+">3+ bd</option>
          </select>
        </div>
        <div style={{ flex: "1 1 90px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Baths</label>
          <select value={filters.baths} onChange={set("baths")} style={field}>
            <option value="any">Any</option><option value="1">1+ ba</option><option value="2">2+ ba</option><option value="3+">3+ ba</option>
          </select>
        </div>
        <div style={{ flex: "1 1 160px", display: "flex", flexDirection: "column", gap: 4 }}>
          <label style={label}>Sort</label>
          <select value={filters.sort} onChange={set("sort")} aria-label="Sort listings" style={field}>
            <option value="recommended">Recommended</option><option value="price_asc">Price: Low to High</option><option value="price_desc">Price: High to Low</option>
          </select>
        </div>
        {changed && (
          <button onClick={() => onChange(CITY_FILTERS_DEFAULT)} style={{ background: "none", border: "none", color: "#F97316", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 4px" }}>Clear filters</button>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
        {[["all", "All sources"], ...Object.entries(sourceLabel).filter(([k]) => sourceCounts[k])].map(([k, name]) => {
          const active = filters.source === k;
          return (
            <button key={k} onClick={() => onChange({ ...filters, source: k })}
              style={{ display: "flex", alignItems: "center", gap: 5, background: active ? "#0f1f3d" : "white", color: active ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {k !== "all" && <span style={{ width: 7, height: 7, borderRadius: "50%", background: sourceColor[k], display: "inline-block" }} />}
              {name}{k !== "all" ? ` (${sourceCounts[k]})` : ""}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Keep in sync with HOW_IT_WORKS in netlify/edge-functions/seo.js.
const HOW_IT_WORKS = [
  { icon: "search", title: "One search, every site", text: "Type a city, neighborhood or zip once. RentHound checks Zillow, Redfin, Realtor.com, Apartments.com and local landlords at the same time and removes the duplicates." },
  { icon: "chart", title: "Compare side by side", text: "Filter by price, bedrooms and bathrooms, sort by rent, and see which site each listing came from before you click through." },
  { icon: "heart", title: "Save and share", text: "Heart the places you like to keep them on your Saved page, and send any listing to a roommate with one link." },
];

// What the homepage shows under the search box before anyone searches.
function HomeContent({ setPage }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => { fetchBlogList().then(setPosts); }, []);
  const latest = posts.filter(p => p.category !== "city-guides").slice(0, 6);
  const rentByCity = posts.filter(p => p.category === "city-guides");
  const sectionTitle = { fontSize: 20, fontWeight: 800, color: "#0f1f3d", margin: "0 0 1rem", letterSpacing: "-0.3px" };
  return (
    <div style={{ marginTop: "1rem" }}>
      <h2 style={sectionTitle}>How RentHound works</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "1rem", marginBottom: "2.5rem" }}>
        {HOW_IT_WORKS.map(s => (
          <div key={s.title} style={{ background: "white", border: "1px solid #ebe8e2", borderRadius: 14, padding: "1.25rem" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#fff7ed", color: "#F97316", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}><Icon name={s.icon} size={20} /></div>
            <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#111" }}>{s.title}</h3>
            <p style={{ margin: 0, fontSize: 14, color: "#4b5563", lineHeight: 1.6 }}>{s.text}</p>
          </div>
        ))}
      </div>
      {latest.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
            <h2 style={sectionTitle}>Guides for renters and landlords</h2>
            <a href="/blog" onClick={e => { e.preventDefault(); window.history.pushState({}, "", "/blog"); setPage("blog"); window.scrollTo(0, 0); }} style={{ color: "#F97316", fontSize: 14, fontWeight: 600, textDecoration: "none", whiteSpace: "nowrap" }}>All guides →</a>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1rem", marginBottom: "2.5rem" }}>
            {latest.map(p => (
              <a key={p.slug} href={`/blog/${p.slug}`} onClick={openBlogPost(setPage, p.slug)} className="rh-card" style={{ display: "block", background: "white", border: "1px solid #ebe8e2", borderRadius: 14, padding: "1.1rem 1.25rem", textDecoration: "none" }}>
                <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 15, color: "#0f1f3d", lineHeight: 1.4 }}>{p.title}</p>
                {p.excerpt && <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.55 }}>{p.excerpt}</p>}
              </a>
            ))}
          </div>
        </>
      )}
      {rentByCity.length > 0 && (
        <>
          <h2 style={sectionTitle}>Average rent by city</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "1rem" }}>
            {rentByCity.map(p => (
              <a key={p.slug} href={`/blog/${p.slug}`} onClick={openBlogPost(setPage, p.slug)} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", fontSize: 13, color: "#374151", textDecoration: "none", fontWeight: 500 }}>
                {p.city ? `Average rent in ${p.city}` : p.title}
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NotFoundPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  useSeo({ title: "Page not found — RentHound", description: "This page doesn't exist on RentHound.", path: window.location.pathname, noindex: true });
  const go = (path, page) => (e) => { e.preventDefault(); window.history.pushState({}, "", path); setPage(page); };
  const button = { display: "inline-block", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, textDecoration: "none" };
  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4", display: "flex", flexDirection: "column" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ flex: 1, maxWidth: 640, margin: "0 auto", padding: "4rem 1.5rem", textAlign: "center" }}>
        <IconBadge name="search" color="#6b7280" background="#f3f4f6" />
        <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0f1f3d", margin: "0 0 8px" }}>We couldn't find that page</h1>
        <p style={{ color: "#6b7280", fontSize: 15, margin: "0 0 1.5rem" }}>The link may be old or mistyped. Try a search, or browse rentals by city.</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: "2rem" }}>
          <a href="/" onClick={go("/", "home")} style={{ ...button, background: "#F97316", color: "white" }}>Search rentals</a>
          <a href="/blog" onClick={go("/blog", "blog")} style={{ ...button, background: "white", color: "#374151", border: "1px solid #e5e7eb" }}>Read the guides</a>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
          {Object.values(CITIES).map(c => (
            <a key={c.slug} href={`/rentals/${c.slug}`} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "#374151", textDecoration: "none", fontWeight: 500 }}>{c.name.split(",")[0]}</a>
          ))}
        </div>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

function SavedPage({ setPage, user, onSignIn, onSignOut, onPostListing }) {
  const [saved, toggleSave, replaceSaved] = useSavedListings();
  const [selectedListing, openListing, closeListing] = useListingHistory("saved");
  const [lookupDone, setLookupDone] = useState(false);
  useSeo({ title: "Saved rentals — RentHound", description: "The rentals you've saved on RentHound.", path: "/saved", noindex: true });

  // Look up hearts saved before listing details were kept; drop any that no longer exist.
  useEffect(() => {
    const ids = Object.keys(readSaved()).filter(id => readSaved()[id] === true);
    if (ids.length === 0) return;
    Promise.all(ids.map(id => fetch(`${API_URL}/api/listings/shared/${encodeURIComponent(id)}`).then(r => r.json()).then(d => [id, d.listing || null]).catch(() => [id, "offline"])))
      .then(results => replaceSaved(current => {
        const next = { ...current };
        for (const [id, listing] of results) {
          if (next[id] !== true || listing === "offline") continue; // keep it and try again next visit
          if (listing) next[id] = { ...listing, savedAt: 0 };
          else delete next[id];
        }
        return next;
      }))
      .finally(() => setLookupDone(true));
  }, [replaceSaved]);

  const listings = Object.values(saved).filter(l => l && typeof l === "object").sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
  const lookingUp = !lookupDone && Object.values(saved).some(v => v === true);
  const goSearch = () => { window.history.pushState({}, "", "/"); setPage("home"); };

  if (selectedListing) {
    return <ListingDetail key={selectedListing.id} listing={selectedListing} allListings={listings} onBack={closeListing} onSelectListing={openListing} setPage={setPage} user={user} onSignIn={onSignIn} onSignOut={onSignOut} onPostListing={onPostListing} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0f1f3d", margin: "0 0 4px", letterSpacing: "-0.5px" }}>Saved rentals</h1>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 1.75rem" }}>
          {listings.length > 0 ? `${listings.length} saved · Click a heart again to remove it.` : "Rentals you heart show up here."} Saved on this device only.
        </p>
        {listings.length === 0 && !lookingUp ? (
          <div style={{ textAlign: "center", padding: "3rem 0", color: "#6b7280" }}>
            <IconBadge name="heart" />
            <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#111" }}>Nothing saved yet</h2>
            <p style={{ margin: "0 0 1.25rem", fontSize: 15 }}>Tap the heart on any listing to keep it here.</p>
            <button onClick={goSearch} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Search rentals</button>
          </div>
        ) : listings.length === 0 ? <ListingSkeletons count={3} /> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }}>
            {listings.map(l => <ListingCard key={l.id} l={l} saved onSave={toggleSave} onClick={openListing} />)}
          </div>
        )}
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

function CityPage({ city, setPage, user, onSignIn, onSignOut, onPostListing }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, openListing, closeListing] = useListingHistory("city");
  const [saved, toggleSave] = useSavedListings();
  const [area, setArea] = useState(null); // a neighborhood chip, or null for the whole city
  const [filters, setFilters] = useState(CITY_FILTERS_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const resultsCache = useRef({}); // so going back to a neighborhood already searched doesn't search again
  const [guides, setGuides] = useState([]);
  useEffect(() => { fetchBlogList().then(posts => setGuides(cityGuides(posts, city.slug))); }, [city.slug]);

  useSeo({
    title: `${city.headline} | RentHound`,
    description: city.description,
    path: `/rentals/${city.slug}`,
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: city.headline, description: city.description, url: `${SITE_URL}/rentals/${city.slug}`, about: { "@type": "City", name: city.name } },
  });

  useEffect(() => {
    const searchLocation = area ? `${area}, ${city.name}` : city.name;
    setCurrentPage(1);
    setFilters(f => ({ ...f, source: "all" }));
    if (resultsCache.current[searchLocation]) {
      setListings(resultsCache.current[searchLocation]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const fetchListings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/listings?location=${encodeURIComponent(searchLocation)}`);
        const data = await res.json();
        if (cancelled) return;
        resultsCache.current[searchLocation] = data.listings || [];
        setListings(data.listings || []);
      } catch (err) { console.error(err); }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchListings();
    return () => { cancelled = true; };
  }, [city, area]);

  const sourceCounts = useMemo(() => {
    const counts = {};
    for (const l of listings) counts[l.source] = (counts[l.source] || 0) + 1;
    return counts;
  }, [listings]);
  const visibleListings = useMemo(() => filterCityListings(listings, filters), [listings, filters]);
  const totalPages = Math.ceil(visibleListings.length / PAGE_SIZE);
  const pageListings = visibleListings.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const areaName = area || city.name.split(",")[0];
  const changeFilters = (f) => { setFilters(f); setCurrentPage(1); };
  const goToPage = (pg) => { setCurrentPage(pg); window.scrollTo({ top: 0, behavior: "smooth" }); };


  if (selectedListing) {
    return <ListingDetail key={selectedListing.id} listing={selectedListing} allListings={listings} onBack={closeListing} onSelectListing={openListing} setPage={setPage} user={user} onSignIn={onSignIn} onSignOut={onSignOut} onPostListing={onPostListing} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4" }}>
      <Nav user={user} onSignIn={onSignIn} onSignOut={onSignOut} setPage={setPage} onPostListing={onPostListing} />
      <div style={{ background: "linear-gradient(135deg, #0f1f3d 0%, #1a3566 100%)", padding: "3rem 2rem", textAlign: "center" }}>
        <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, margin: "0 0 8px" }}>
          <a href="/" style={{ color: "rgba(255,255,255,0.5)", textDecoration: "none" }}>RentHound</a> › Rentals › {city.name}
        </p>
        <h1 style={{ color: "white", fontSize: 32, fontWeight: 700, margin: "0 0 0.75rem", letterSpacing: "-0.5px" }}>{city.headline}</h1>
        <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 15, maxWidth: 600, margin: "0 auto 1.5rem" }}>{city.description}</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          {[null, ...city.neighborhoods].map(n => {
            const active = area === n;
            return (
              <button key={n || "all"} onClick={() => setArea(n)} aria-pressed={active}
                style={{ background: active ? "#F97316" : "rgba(255,255,255,0.1)", color: active ? "white" : "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: active ? 600 : 400, padding: "4px 12px", borderRadius: 20, border: active ? "1px solid #F97316" : "1px solid rgba(255,255,255,0.2)", cursor: "pointer" }}>
                {n || `All of ${city.name.split(",")[0]}`}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ background: "white", borderBottom: "1px solid #e5e7eb", padding: "1rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: "8px 24px", fontSize: 13, color: "#6b7280" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="pin" size={15} color="#9ca3af" />{area ? `${area}, ${city.name}` : city.name}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="wallet" size={15} color="#9ca3af" />Avg rent: {city.avgRent}/mo</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="home" size={15} color="#9ca3af" />{loading ? "Loading..." : `${listings.length} listings found`}</span>
        </div>
      </div>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "2rem 1.5rem 4rem" }}>
        {loading ? <ListingSkeletons /> : listings.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem 0", color: "#6b7280" }}>
            <IconBadge name="search" color="#6b7280" background="#f3f4f6" />
            <p style={{ fontSize: 16, margin: 0 }}>No listings found in {areaName} right now. {area ? "Try another neighborhood." : "Check back soon!"}</p>
          </div>
        ) : (
          <>
            <CityFilterBar filters={filters} onChange={changeFilters} sourceCounts={sourceCounts} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.25rem" }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "#111" }}>
                {visibleListings.length === listings.length ? listings.length : `${visibleListings.length} of ${listings.length}`} rentals found in {area ? `${area}, ${city.name}` : city.name}
              </h2>
            </div>
            {visibleListings.length === 0 ? (
              <div style={{ textAlign: "center", padding: "3rem 0", color: "#6b7280" }}>
                <p style={{ fontSize: 15, margin: "0 0 12px" }}>No rentals match these filters.</p>
                <button onClick={() => changeFilters(CITY_FILTERS_DEFAULT)} style={{ background: "white", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer" }}>Clear filters</button>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }}>
                  {pageListings.map(l => <ListingCard key={l.id} l={l} saved={saved[l.id]} onSave={toggleSave} onClick={openListing} />)}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: "2rem" }}>
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                      style={{ background: currentPage === 1 ? "#f3f4f6" : "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: currentPage === 1 ? "default" : "pointer", color: currentPage === 1 ? "#9ca3af" : "#374151", fontWeight: 500 }}>← Previous</button>
                    <span style={{ fontSize: 14, color: "#6b7280", fontWeight: 500 }}>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}
                      style={{ background: currentPage >= totalPages ? "#f3f4f6" : "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: currentPage >= totalPages ? "default" : "pointer", color: currentPage >= totalPages ? "#9ca3af" : "#374151", fontWeight: 500 }}>Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
        <div style={{ background: "white", borderRadius: 12, border: "1px solid #e5e7eb", padding: "2rem", marginTop: "2rem" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: "#111", margin: "0 0 1rem" }}>Renting in {city.name.split(",")[0]}</h2>
          <p style={{ color: "#374151", fontSize: 15, lineHeight: 1.8, margin: "0 0 1rem" }}>
            {city.name.split(",")[0]} offers a diverse range of rental options. Popular neighborhoods include {city.neighborhoods.slice(0, 3).join(", ")}, and more. The average rent is around {city.avgRent} per month.
          </p>
          {guides.length > 0 && (
            <div style={{ margin: "0 0 1.5rem" }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111", margin: "0 0 0.75rem" }}>Guides for {city.name.split(",")[0]} renters</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0.75rem" }}>
                {guides.map(g => (
                  <a key={g.slug} href={`/blog/${g.slug}`} onClick={openBlogPost(setPage, g.slug)} style={{ display: "block", background: "#f8f7f4", border: "1px solid #ebe8e2", borderRadius: 10, padding: "0.9rem 1rem", textDecoration: "none" }}>
                    <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 14, color: "#0f1f3d", lineHeight: 1.4 }}>{g.title}</p>
                    {g.excerpt && <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>{g.excerpt}</p>}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={onPostListing} style={{ background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>List your {city.name.split(",")[0]} rental →</button>
            <button onClick={() => { window.history.pushState({}, "", "/"); setPage("home"); }} style={{ background: "white", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: "pointer" }}>Search all cities</button>
          </div>
        </div>
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: "#6b7280", margin: "0 0 1rem" }}>Browse rentals in other cities</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.values(CITIES).filter(c => c.slug !== city.slug).map(c => (
              <a key={c.slug} href={`/rentals/${c.slug}`} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 16px", fontSize: 13, color: "#374151", textDecoration: "none", fontWeight: 500 }}>{c.name.split(",")[0]}</a>
            ))}
          </div>
        </div>
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}

export default function RentHound() {
  const cityFromPath = getCityFromPath();
  const blogSlug = getBlogSlugFromPath();

  const [page, setPageState] = useState(pageFromLocation);
  const [, setNavCount] = useState(0); // re-renders when only the address changed, e.g. one blog post to another

  // Switching pages also changes the address, so the browser's Back and Forward buttons
  // move between pages; when they do, show the page for the new address.
  const setPage = useCallback((p) => {
    const path = PAGE_PATHS[p];
    if (path && window.location.pathname !== path) window.history.pushState({}, "", path);
    setPageState(p);
    setNavCount(n => n + 1);
  }, []);
  useEffect(() => {
    const onPop = () => { setPageState(pageFromLocation()); setNavCount(n => n + 1); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [allListings, setAllListings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("recommended");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [location, setLocation] = useState("");
  const [type, setType] = useState("all");
  const [maxPrice, setMaxPrice] = useState("");
  const [beds, setBeds] = useState("any");
  const [baths, setBaths] = useState("any");
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [saved, toggleSave] = useSavedListings();
  const [selectedListing, openListing, closeListing, setSelectedListing] = useListingHistory("home");
  const [sharedLinkError, setSharedLinkError] = useState(null);
  const [user, setUser] = useState(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showPostListing, setShowPostListing] = useState(false);

  // Every switch to another page starts at its top, wherever the link was clicked.
  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const listingId = params.get("listing");
    if (listingId) {
      fetch(`${API_URL}/api/listings/shared/${encodeURIComponent(listingId)}`)
        .then(r => r.json())
        .then(data => {
          if (data.listing) setSelectedListing(data.listing);
          else setSharedLinkError(data.error || "This listing is no longer available.");
        }).catch(() => setSharedLinkError("Could not load this listing. Please try again."));
    }
  }, [setSelectedListing]);

  const handleSignOut = async () => { await supabase.auth.signOut(); setUser(null); setPage("home"); };

  const fetchListings = useCallback(async () => {
    setLoading(true); setError(null); setSearched(true); setCurrentPage(1);
    try {
      const params = new URLSearchParams();
      if (location) params.set("location", location);
      if (type !== "all") params.set("type", type);
      if (maxPrice) params.set("maxPrice", maxPrice);
      if (beds !== "any") params.set("beds", beds);
      if (baths !== "any") params.set("baths", baths);
      const res = await fetch(`${API_URL}/api/listings?${params.toString()}`);
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const all = data.listings || [];
      setAllListings(all); setTotal(all.length); setSourceFilter("all");
    } catch (err) {
      setError("Could not load listings. Make sure the backend is running.");
      setAllListings([]);
    } finally { setLoading(false); }
  }, [location, type, maxPrice, beds, baths]);

  const sourceCounts = useMemo(() => {
    const counts = {};
    for (const l of allListings) counts[l.source] = (counts[l.source] || 0) + 1;
    return counts;
  }, [allListings]);

  const visibleListings = useMemo(() => {
    const filtered = sourceFilter === "all" ? allListings : allListings.filter(l => l.source === sourceFilter);
    if (sortBy === "recommended") return filtered;
    const dir = sortBy === "price_asc" ? 1 : -1;
    // Listings without a price always go last, whichever direction we sort.
    return [...filtered].sort((a, b) => {
      if (!a.price && !b.price) return 0;
      if (!a.price) return 1;
      if (!b.price) return -1;
      return (a.price - b.price) * dir;
    });
  }, [allListings, sourceFilter, sortBy]);

  const listings = visibleListings.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const goToPage = (pg) => {
    setCurrentPage(pg);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const totalPages = Math.ceil(visibleListings.length / PAGE_SIZE);
  const handlePostListing = () => {
    // Close any open listing first; otherwise the listing page covers the Post a listing form.
    setSelectedListing(null);
    if (window.location.search.includes("listing=")) window.history.replaceState({}, "", "/");
    setPage("home");
    setShowPostListing(true);
  };

  if (page === "about") return <AboutPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "privacy") return <PrivacyPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "terms") return <TermsPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "pricing") return <PricingPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "blog") return <BlogPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "blogpost") return <BlogPostPage key={blogSlug} slug={blogSlug} setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "notfound") return <NotFoundPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "saved") return <SavedPage setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "dashboard") return <Dashboard user={user} setPage={setPage} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  if (page === "admin") return <AdminPage setPage={setPage} />;
  if (page === "city" && cityFromPath) return <CityPage key={cityFromPath.slug} city={cityFromPath} setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;

  if (selectedListing) {
    return <ListingDetail key={selectedListing.id} listing={selectedListing} allListings={allListings} onBack={closeListing} onSelectListing={openListing} setPage={setPage} user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} onPostListing={handlePostListing} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8f7f4", color: "#1a1a1a" }}>
      {showSignIn && <SignInModal onClose={() => setShowSignIn(false)} />}
      {showPostListing && <PostListingModal user={user} onClose={() => setShowPostListing(false)} onSignIn={() => { setShowPostListing(false); setShowSignIn(true); }} />}
      <Nav user={user} onSignIn={() => setShowSignIn(true)} onSignOut={handleSignOut} setPage={setPage} onPostListing={() => setShowPostListing(true)} />

      <div style={{ background: "linear-gradient(135deg, #0f1f3d 0%, #1a3566 100%)", padding: "3rem 2rem 3.5rem", textAlign: "center" }}>
        <h1 style={{ color: "white", fontSize: 36, fontWeight: 700, margin: "0 0 0.5rem", letterSpacing: "-0.5px" }}>Every rental, one search</h1>
        <p style={{ color: "rgba(255,255,255,0.65)", margin: "0 0 2rem", fontSize: 16 }}>Apartments and houses from Zillow, Redfin, Realtor.com, Apartments.com and local landlords</p>
        <div style={{ maxWidth: 880, margin: "0 auto", background: "white", borderRadius: 12, padding: "1rem 1.25rem", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" }}>
          <div style={{ flex: "2 1 180px", display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Location</label>
            <input value={location} onChange={e => setLocation(e.target.value)} onKeyDown={e => e.key === "Enter" && fetchListings()} placeholder="City, neighborhood, or zip"
              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 14, outline: "none", color: "#111" }} />
          </div>
          <div style={{ flex: "1 1 110px", display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Type</label>
            <select value={type} onChange={e => setType(e.target.value)} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 14, background: "white", color: "#111" }}>
              <option value="all">All types</option><option value="apartment">Apartment</option><option value="house">House</option><option value="storage">Storage</option>
            </select>
          </div>
          <div style={{ flex: "1 1 110px", display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Max price/mo</label>
            <input value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="$3,000" type="number"
              style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 14, outline: "none", color: "#111" }} />
          </div>
          <div style={{ flex: "1 1 90px", display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Beds</label>
            <select value={beds} onChange={e => setBeds(e.target.value)} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 14, background: "white", color: "#111" }}>
              <option value="any">Any</option><option value="0">Studio</option><option value="1">1 bd</option><option value="2">2 bd</option><option value="3+">3+ bd</option>
            </select>
          </div>
          <div style={{ flex: "1 1 90px", display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Baths</label>
            <select value={baths} onChange={e => setBaths(e.target.value)} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px", fontSize: 14, background: "white", color: "#111" }}>
              <option value="any">Any</option><option value="1">1 ba</option><option value="2">2 ba</option><option value="3+">3+ ba</option>
            </select>
          </div>
          <button onClick={fetchListings} style={{ flex: "0 0 auto", background: "#F97316", color: "white", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" fill="none" viewBox="0 0 14 14"><circle cx="6" cy="6" r="4.5" stroke="white" strokeWidth="1.5"/><line x1="9.5" y1="9.5" x2="12.5" y2="12.5" stroke="white" strokeWidth="1.5" strokeLinecap="round"/></svg>
            Search
          </button>
        </div>
      </div>

      <div style={{ background: "#f8f7f4", borderBottom: "1px solid #e5e7eb", padding: "1rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Browse by city</p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.values(CITIES).map(c => (
              <a key={c.slug} href={`/rentals/${c.slug}`} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "#374151", textDecoration: "none", fontWeight: 500 }}>
                {c.name.split(",")[0]}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "1.5rem 1.5rem 3rem" }}>
        {sharedLinkError && !searched && <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "0.75rem 1.25rem", color: "#92400e", fontSize: 14, marginBottom: "1rem" }}>{sharedLinkError} Search above to find similar rentals.</div>}
        {!searched && (
          <div style={{ textAlign: "center", padding: "1.5rem 0 2rem", color: "#6b7280" }}>
            <HoundIllustration />
            <h2 style={{ margin: "1rem 0 6px", fontSize: 20, fontWeight: 700, color: "#111" }}>Let's sniff out your next rental</h2>
            <p style={{ margin: 0, fontSize: 15 }}>Enter a city, neighborhood, or zip above and hit Search.</p>
          </div>
        )}
        {!searched && <HomeContent setPage={setPage} />}
        {loading && <ListingSkeletons />}
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "1rem 1.25rem", color: "#991b1b", fontSize: 14 }}>{error}</div>}
        {!loading && searched && !error && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: "1.25rem" }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "#111" }}>
                {sourceFilter === "all" ? total : `${visibleListings.length} of ${total}`} rental{total !== 1 ? "s" : ""} found{location ? ` near "${location}"` : ""}
              </h2>
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {[["all", "All", total], ...Object.entries(sourceLabel).filter(([k]) => sourceCounts[k]).map(([k, v]) => [k, v, sourceCounts[k]])].map(([k, label, count]) => {
                  const active = sourceFilter === k;
                  return (
                    <button key={k} onClick={() => { setSourceFilter(k); setCurrentPage(1); }}
                      style={{ display: "flex", alignItems: "center", gap: 5, background: active ? "#0f1f3d" : "white", color: active ? "white" : "#374151", border: "1px solid #e5e7eb", borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                      {k !== "all" && <span style={{ width: 7, height: 7, borderRadius: "50%", background: sourceColor[k], display: "inline-block" }} />}
                      {label} ({count})
                    </button>
                  );
                })}
                <select value={sortBy} onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }} aria-label="Sort listings"
                  style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "6px 10px", fontSize: 13, color: "#374151", background: "white", cursor: "pointer" }}>
                  <option value="recommended">Sort: Recommended</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                </select>
              </div>
            </div>
            {listings.length === 0 ? (
              <div style={{ textAlign: "center", padding: "4rem 0", color: "#6b7280" }}>
                <IconBadge name="search" color="#6b7280" background="#f3f4f6" />
                <p style={{ fontSize: 16, margin: 0 }}>No listings found. Try a different location or filters.</p>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }}>
                  {listings.map(l => <ListingCard key={l.id} l={l} saved={saved[l.id]} onSave={toggleSave} onClick={openListing} />)}
                </div>
                {totalPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: "2rem" }}>
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
                      style={{ background: currentPage === 1 ? "#f3f4f6" : "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: currentPage === 1 ? "default" : "pointer", color: currentPage === 1 ? "#9ca3af" : "#374151", fontWeight: 500 }}>← Previous</button>
                    <span style={{ fontSize: 14, color: "#6b7280", fontWeight: 500 }}>Page {currentPage} of {totalPages}</span>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}
                      style={{ background: currentPage >= totalPages ? "#f3f4f6" : "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 20px", fontSize: 14, cursor: currentPage >= totalPages ? "default" : "pointer", color: currentPage >= totalPages ? "#9ca3af" : "#374151", fontWeight: 500 }}>Next →</button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <Footer setPage={setPage} />
    </div>
  );
}